import axios from "axios"
import { useEffect, useRef, useState } from "react"
import { useStoreState } from "easy-peasy"
import { beep } from "./beep.js"
import ErrorPopup from "./ErrorPopup.js"
import StatusPopup from "./StatusPopup.js"

import { cap_Name, title_Case, valid_Email, valid_PhoneNumber, valid_state_code } from "./Validate"
import {
	FARM_DISTRICT_MAP_NAME_URL,
	FARM_OWNER_SEARCH_URL,
	FARM_OWNER_LOAD_URL,
	FARM_OWNER_ADD_URL,
	FARM_OWNER_EDIT_URL,
	FARM_APPLICATION_LOAD_URL,
	FARM_APPLICATION_SAVE_URL,
	FARM_APPLICATION_OPERATOR_SAVE_URL,
	FARM_PRODUCTION_SAVE_URL,
	FARM_QUALIFICATION_SAVE_URL,
	FARM_BEES_SAVE_URL,
	FARM_POULTRY_SAVE_URL,
	FARM_LIVESTOCK_SAVE_URL,
	FARM_CROPS_SAVE_URL,
	FARM_HAY_PRODUCTION_SAVE_URL,
	FARM_PARCEL_LOAD_URL,
	FARM_PARCEL_SAVE_ALL_URL,
	FARM_ROLL_FORWARD_URL,
	FARM_APPLICATION_DELETE_URL,
	FARM_SECTION_REVIEWED_URL,
	FARM_APP_SECTIONS_LOAD_URL,
	FARM_APP_SECTIONS_SAVE_URL,
} from "./Constants.js"


// WV fiscal year: July 1 – June 30.  FY 2026 = Jul 2025 – Jun 2026.
// If current month is July (6) or later the new fiscal year has started.
const _fy = new Date()
const CURRENT_FY = _fy.getMonth() >= 6 ? _fy.getFullYear() + 1 : _fy.getFullYear()
// Display a fiscal year (stored as its END year) as a "2026 - 2027" span.
const fyLabel = (endYear) => `${endYear - 1} - ${endYear}`

const CROP_NAMES = [
	"Field Corn", "Soybeans", "Wheat", "Alfalfa & Alfalfa Mix Hay (Dry)", "Hay & Haylage",
	"Permanent Pasture", "Berries", "Grapes", "Tobacco", "Lavender", "Hemp", "Medical Marijuana",
	"Speltz", "Oats",
]

const STANDARD_CROPS = [
	{ name: "Field Corn",                      key: "corn_acres" },
	{ name: "Soybeans",                        key: "soybean_acres" },
	{ name: "Wheat",                           key: "wheat_acres" },
	{ name: "Alfalfa & Alfalfa Mix Hay (Dry)", key: "alfalfa_acres" },
	{ name: "Hay & Haylage",                             key: "hay_acres" },
	{ name: "Permanent Pasture",               key: "pasture_acres" },
]
const STANDARD_CROP_NAMES = new Set(STANDARD_CROPS.map((c) => c.name))
const DEFAULT_CROP_ROWS = STANDARD_CROPS.map((c, i) => ({ id: i + 1, name: c.name, acres: 0, sqft: 0, vegetable: false, high_tunnel: false }))

const LIVESTOCK_MAP = [
	{ name: "Beef Cattle",    field: "beef_cattle" },
	{ name: "Dairy Cattle",   field: "dairy_cattle" },
	{ name: "Sheep/Lambs",    field: "sheep_lambs" },
	{ name: "Goats/Kids",     field: "goats_kids" },
	{ name: "Hogs/Pigs",      field: "hogs_pigs" },
	{ name: "Equine",         field: "equine" },
	{ name: "Hens/Pullets",   field: "hens_pullets" },
	{ name: "Broilers",       field: "broilers" },
	{ name: "Turkeys",        field: "turkeys" },
]

const OTHER_LIVESTOCK_TYPES = [
	"Beef Cattle & Calves", "Dairy Cattle & Calves", "Sheep & Lambs", "Goats & Kids",
	"Hogs & Pigs", "Equine (horses, donkeys, mules)", "Fish", "Emus", "Ostriches", "Llamas",
]
const DEFAULT_OTHER_LIVESTOCK = OTHER_LIVESTOCK_TYPES.slice(0, 6).map((t, i) => ({ id: i + 1, animal_type: t, count: 0 }))

const PARCEL_DISTRICTS = ["Beech Bottom", "Bethany", "Buffalo", "Cross Creek", "Follansbee", "Weirton", "Wellsburg", "Windsor Heights"]

const FRUIT_TREE_TYPES = [
	"Apple", "Pear", "Plum", "Peach", "Persimmon", "Pawpaw", "Mulberry",
	"Black Walnut", "Butternut", "Chestnut", "Hazelnut", "Hickory", "Pignut",
	"Beech", "Birch", "Black Cherry", "Black Locust", "Christmas", "Maple", "Nursery", "Oak", "Yellow-Poplar",
]
const SAP_TREE_TYPES = ["Black Walnut", "Box Elder", "Red Maple", "Red Oak", "Sugar Maple"]
const TREE_TYPES = ["Apple", "Peach", "Christmas", "Nurseries"]


// ── Shared helpers ────────────────────────────────────────────────────────────


const SectionHead = ({ title }) => (
	<div style={{ fontWeight: "bold", borderBottom: "1px solid #aaa", marginBottom: "0.5rem", marginTop: "1rem", paddingBottom: "2px", color: "var(--text-muted, #555)", letterSpacing: "0.03em" }}>
		{title}
	</div>
)

const Row = ({ label, children, tight }) => (
	<div style={{ display: "flex", alignItems: "center", marginBottom: tight ? "0.25rem" : "0.4rem", gap: "0.5rem" }}>
		<span style={{ minWidth: "180px", textAlign: "right", paddingRight: "0.5rem" }}>{label}</span>
		{children}
	</div>
)

// ── Owner helpers ─────────────────────────────────────────────────────────────

const blankOwner = {
	account_id: "", owner_name: "", operation_name: "",
	email: "", phone: "",
	number_addr1: "", street_addr1: "", addr2: "", city_name: "",
	state_code: "WV", zip: "", tax_district: "", notes: "",
	operator_is_owner: true,
	operator_name: "", operator_number_addr1: "", operator_street_addr1: "",
	operator_addr2: "", operator_city: "", operator_state: "WV",
	operator_zip: "", operator_phone: "", operator_email: "",
}

// ── Component ─────────────────────────────────────────────────────────────────

const Farm = () => {
	const Token = useStoreState((state) => state.Token)
	const DistrictList = useStoreState((state) => state.DistrictList)
	const ParcelDistrictList = useStoreState((state) => state.ParcelDistrictList)
	const Theme = useStoreState((state) => state.Theme)

	const [searchFiscalYear, setSearchFiscalYear] = useState(CURRENT_FY)
	const [searchPendingOnly, setSearchPendingOnly] = useState(false)
	const [searchValue, setSearchValue] = useState("")
	const [searchStatus, setSearchStatus] = useState("")
	const [searchResults, setSearchResults] = useState([])
	const [searchError, setSearchError] = useState("")
	const searchResultsRef = useRef(null)
	const [resultsMaxHeight, setResultsMaxHeight] = useState(null)

	const [owner, setOwner] = useState(null)
	const [ownerForm, setOwnerForm] = useState(blankOwner)
	const [ownerStatus, setOwnerStatus] = useState("")
	const [ownerError, setOwnerError] = useState("")
	const [ownerMode, setOwnerMode] = useState("view")

	useEffect(() => { if (searchError) beep() }, [searchError])
	useEffect(() => {
		if (searchStatus !== "empty") return
		const t = setTimeout(() => setSearchStatus(""), 2000)
		return () => clearTimeout(t)
	}, [searchStatus])
	useEffect(() => { if (ownerError) beep() }, [ownerError])
	const [applications, setApplications] = useState([])

	const [activeSection, setActiveSection] = useState(null)
	const [parcels, setParcels] = useState([])
	const [opFormStatus, setOpFormStatus] = useState("")
	const [opFormError, setOpFormError] = useState("")
	const [opDescStatus, setOpDescStatus] = useState("")
	const [opDescError, setOpDescError] = useState("")
	const [hayStatus, setHayStatus] = useState("")
	const [hayError, setHayError] = useState("")
	const [cropsStatus, setCropsStatus] = useState("")
	const [cropsError, setCropsError] = useState("")
	const [treesStatus, setTreesStatus] = useState("")
	const [treesSaveSource, setTreesSaveSource] = useState("")
	const [treesError, setTreesError] = useState("")
	const [rollForwardStatus, setRollForwardStatus] = useState("")
	const [rollForwardError, setRollForwardError] = useState("")
	const [isRolledForward, setIsRolledForward] = useState(false)
	const [rolledForwardYear, setRolledForwardYear] = useState(null)
	const [showRollForwardConfirm, setShowRollForwardConfirm] = useState(false)
	const [deleteStatus, setDeleteStatus] = useState("")
	const [deleteError, setDeleteError] = useState("")
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [sectionsReviewed, setSectionsReviewed] = useState({})
	const [showReviewComplete, setShowReviewComplete] = useState(false)
	const reviewCompleteShown = useRef(false)
	const BLANK_DOTS = { owner: false, operator: false, acreage: false, parcels: false, crops: false, hay: false, trees: false, sapTrees: false, livestockRows: false, poultry: false, bees: false, prodValues: false, qualification: false }
	const [dotSaved, setDotSaved] = useState(BLANK_DOTS)
	const setDot = (updates) => setDotSaved((prev) => ({ ...prev, ...updates }))
	// A section counts as done when it holds data OR a deputy has explicitly saved
	// it. Without the second test a section saved as all zeros would come back
	// dark on the next load, indistinguishable from one never opened.
	const setLoadDot = (updates, fy = null, reviewed = {}) => setDotSaved((prev) => ({
		...prev,
		...Object.fromEntries(
			Object.entries(updates).map(([k, v]) => [
				k,
				(v || reviewed[k]) ? (fy !== null && v && !reviewed[k] ? "pending" : true) : false,
			])
		),
	}))
	const [livestockStatus, setLivestockStatus] = useState("")
	const [livestockError, setLivestockError] = useState("")
	const [livestockRowsStatus, setLivestockRowsStatus] = useState("")
	const [livestockRowsError, setLivestockRowsError] = useState("")
	const [poultryStatus, setPoultryStatus] = useState("")
	const [poultryError, setPoultryError] = useState("")
	const [beesStatus, setBeesStatus] = useState("")
	const [beesError, setBeesError] = useState("")
	const [prodValueStatus, setProdValueStatus] = useState("")
	const [prodValueError, setProdValueError] = useState("")
	const [qualStatus, setQualStatus] = useState("")
	const [qualError, setQualError] = useState("")
	const [parcelStatus, setParcelStatus] = useState("")
	const [parcelError, setParcelError] = useState("")
	const districtMapCache = useRef({})
	const [sectionsCache, setSectionsCache] = useState(null)
	const [cropRows, setCropRows] = useState(DEFAULT_CROP_ROWS)
	const [hay, setHay] = useState({ bales: 0, lbs_per_bale: 0, rolls: 0, lbs_per_roll: 0 })
	const [treeRows, setTreeRows] = useState([])
	const [treeSapRows, setTreeSapRows] = useState([])
	const [livestock, setLivestock] = useState({ beef_cattle: 0, dairy_cattle: 0, sheep_lambs: 0, goats_kids: 0, hogs_pigs: 0, equine: 0, hens_pullets: 0, broilers: 0, turkeys: 0, poultry_owner: "", bee_colonies: 0, honey_lbs: 0, nucs_sold: 0, queens_sold: 0 })
	const [otherLivestock, setOtherLivestock] = useState(DEFAULT_OTHER_LIVESTOCK)

	const hayChange = (field, val) => setHay((p) => ({ ...p, [field]: val }))
	// Hay production needs a complete bales pair and/or a complete rolls pair before saving
	// Hay: a count without its weight (or the reverse) is a half-finished entry
	// and is blocked. All zeros is a complete answer -- the farm made no hay this
	// year -- and must save, which is how a rolled-forward figure gets cleared.
	function hayProductionIncomplete() {
		const half = (a, b) => (parseFloat(hay[a]) > 0) !== (parseFloat(hay[b]) > 0)
		return half("bales", "lbs_per_bale") || half("rolls", "lbs_per_roll")
	}
	const livestockChange = (field, val) => setLivestock((p) => ({ ...p, [field]: val }))

	function addCropRow() { setCropRows((p) => {
		return [...p, { id: Date.now(), name: "", acres: 0, sqft: 0, vegetable: false, high_tunnel: false }]
	}) }
	function removeCropRow(id) { setCropRows((p) => p.filter((r) => r.id !== id)) }
	function cropRowChange(id, field, val) { setCropRows((p) => p.map((r) => r.id === id ? { ...r, [field]: val } : r)) }
	// An added (non-standard) crop row needs a name. Its acreage may legitimately
	// be zero -- the crop is still grown but produced nothing this year -- so only
	// a nameless row is incomplete.
	function addedCropRowMissingValue(row) {
		return !STANDARD_CROP_NAMES.has(row.name) && !String(row.name || "").trim()
	}

	function addTreeRow() { setTreeRows((p) => [...p, { id: Date.now(), tree_type: "", tree_count: 0, acres: 0 }]) }
	function removeTreeRow(id) { setTreeRows((p) => p.filter((r) => r.id !== id)) }
	function treeRowChange(id, field, val) { setTreeRows((p) => p.map((r) => r.id === id ? { ...r, [field]: val } : r)) }


	const [sapTreeRows, setSapTreeRows] = useState([])
	function addSapTreeRow() { setSapTreeRows((p) => [...p, { id: Date.now(), tree_type: "", tree_count: 0, gallons: 0 }]) }
	function removeSapTreeRow(id) { setSapTreeRows((p) => p.filter((r) => r.id !== id)) }
	function sapTreeRowChange(id, field, val) { setSapTreeRows((p) => p.map((r) => r.id === id ? { ...r, [field]: val } : r)) }

	const [cropValue, setCropValue] = useState("")
	const [produceValue, setProduceValue] = useState("")
	const [livestockValue, setLivestockValue] = useState("")
	const [totalValue, setTotalValue] = useState("")

	// Agricultural production: the value produced, which is what the A-D
	// qualification test is about. Farm income is a separate figure that the
	// deputy may overwrite, so it cannot stand in for this.
	const productionTotal =
		(parseFloat(cropValue.replace(/,/g, "")) || 0) +
		(parseFloat(produceValue.replace(/,/g, "")) || 0) +
		(parseFloat(livestockValue.replace(/,/g, "")) || 0)
	const productionAboveThreshold = productionTotal > 999.99

	const [showQualHelp, setShowQualHelp] = useState(false)
	const [agriTourism, setAgriTourism] = useState(false)
	const [prodA, setProdA] = useState(false)
	const [prodADetail, setProdADetail] = useState("")
	const [prodB, setProdB] = useState(false)
	const [prodBYear, setProdBYear] = useState("")
	const [prodC, setProdC] = useState(false)
	const [prodCCrop, setProdCCrop] = useState("")
	const [prodD, setProdD] = useState(false)
	// Question 2a as a deputy answered it by hand: true, false, or null for
	// unanswered. Only consulted when the income figures cannot settle it.
	const [q2aManual, setQ2aManual] = useState(null)
	const [nonFarmIncome, setNonFarmIncome] = useState("")
	// Farm Income shows the Total Production Value unless a deputy has typed
	// something else: on farms that consume most of what they produce, the value
	// grown and the income earned are different numbers. Hancock reported an
	// Allison heir with $20,000+ of production and $5,000 of income, another
	// with $0.
	//
	// Only the override is state; the suggestion is derived on every render.
	// It used to be copied into state by an effect keyed on the production total,
	// which meant clearing the field left it blank until some production value
	// happened to change -- the total never came back. Deriving it makes the
	// fallback immediate and removes the ordering problem entirely.
	const [farmIncomeOverride, setFarmIncomeOverride] = useState(null)
	const money = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
	const farmIncome = farmIncomeOverride ?? (productionTotal > 0 ? money(productionTotal) : "")
	const [q3Yes, setQ3Yes] = useState(false)
	const [q3No, setQ3No] = useState(false)
	const [q4Yes, setQ4Yes] = useState(false)
	const [q4No, setQ4No] = useState(false)

	// Question 2a -- "is at least 50% of total income from agricultural
	// production?" -- is arithmetic on the two income figures sitting on this
	// same screen, so it is derived rather than asked, and the boxes are locked
	// to stop the answer contradicting the numbers beside it. With no income
	// recorded at all there is no ratio to take, and the deputy answers by hand.
	//
	// Derived on every render, not pushed into state by an effect. The effect was
	// keyed on these two booleans, so moving between two farms that happened to
	// share them fired nothing: the incoming farm's stored answer (NULL since the
	// tri-state migration) stayed on screen as two empty boxes that were also
	// disabled, leaving Save demanding an answer to a question nobody could
	// reach. Hancock hit this on Mike White; entering $0 of non-farm income did
	// not help because it changed neither boolean.
	const q2aTotalIncome = (parseFloat((farmIncome || "").replace(/,/g, "")) || 0) +
		(parseFloat((nonFarmIncome || "").replace(/,/g, "")) || 0)
	const q2aDerivable = q2aTotalIncome > 0
	const q2aAgriculturalMajority =
		((parseFloat((farmIncome || "").replace(/,/g, "")) || 0) * 2) >= q2aTotalIncome
	const q2aYes = q2aDerivable ? q2aAgriculturalMajority : q2aManual === true
	const q2aNo = q2aDerivable ? !q2aAgriculturalMajority : q2aManual === false
	const [officialGranted, setOfficialGranted] = useState("Pending")
	const [officialBy, setOfficialBy] = useState("")

	function addTreeSapRow() { setTreeSapRows((p) => [...p, { id: Date.now(), tree_type: "Apple", tree_count: 0, gallons_produced: 0 }]) }
	function removeTreeSapRow(id) { setTreeSapRows((p) => p.filter((r) => r.id !== id)) }
	function treeSapRowChange(id, field, val) { setTreeSapRows((p) => p.map((r) => r.id === id ? { ...r, [field]: val } : r)) }

	useEffect(() => {
		const has = cropRows.some((r) => r.acres > 0 || r.sqft > 0) ||
			hay.bales > 0 || hay.lbs_per_bale > 0 || hay.rolls > 0 || hay.lbs_per_roll > 0
	}, [cropRows, hay])

	useEffect(() => {
		const has = treeRows.some((r) => r.tree_count > 0 || r.acres > 0) ||
			sapTreeRows.some((r) => r.tree_count > 0 || r.gallons > 0)
	}, [treeRows, sapTreeRows])

	function addOtherLivestock() {
		setOtherLivestock((p) => [...p, { id: Date.now(), animal_type: "", count: 0 }])
	}
	function removeOtherLivestock(id) { setOtherLivestock((p) => p.filter((r) => r.id !== id)) }
	function otherLivestockChange(id, field, val) { setOtherLivestock((p) => p.map((r) => r.id === id ? { ...r, [field]: val } : r)) }

	const [mapNames, setMapNames] = useState({})
	const [mapOptions, setMapOptions] = useState({})
	const [districtNumbers, setDistrictNumbers] = useState({})
	const [opForm, setOpForm] = useState({
		fiscal_year: CURRENT_FY,
		primary_county: false,
		veteran: false,
		status: "Pending",
		operator_name: "",
		number_addr1: "", street_addr1: "", addr2: "",
		city_name: "", state_code: "WV", zip: "",
		email: "", phone: "",
	})
	const opChange = (field) => (e) => setOpForm((p) => ({
		...p,
		[field]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
	}))
	const blankOpForm = { primary_county: false, veteran: false, status: "Pending", operator_name: "", number_addr1: "", street_addr1: "", addr2: "", city_name: "", state_code: "WV", zip: "", email: "", phone: "" }
	const [acresOwned, setAcresOwned] = useState(0)
	const [acresRentedFrom, setAcresRentedFrom] = useState(0)
	const [acresRentedTo, setAcresRentedTo] = useState(0)
	const [acresTotalFarm, setAcresTotalFarm] = useState(0) // { parcelId: mapName }

	function addParcel() { setParcels((p) => [...p, { id: Date.now(), tax_district: "", map_code: "", parcel_id: "", pasture_acres: 0, cropland_acres: 0, woodland_acres: 0, waste_acres: 0, other_acres: 0, rented: false, rent_per_year: 0 }]) }
	function removeParcel(id) {
		setParcels((p) => p.filter((r) => r.id !== id))
		setMapNames((m) => { const n = { ...m }; delete n[id]; return n })
		setDistrictNumbers((d) => { const n = { ...d }; delete n[id]; return n })
	}
	function parcelChange(id, field, val) { setParcels((p) => p.map((r) => r.id === id ? { ...r, [field]: val } : r)) }

	async function handleDistrictChange(parcelId, districtName) {
		parcelChange(parcelId, "tax_district", districtName)
		if (!districtName) {
			setMapOptions((m) => ({ ...m, [parcelId]: [] }))
			setMapNames((m) => ({ ...m, [parcelId]: "" }))
			setDistrictNumbers((d) => ({ ...d, [parcelId]: "" }))
			parcelChange(parcelId, "map_code", "")
			parcelChange(parcelId, "parcel_id", "")
			return
		}
		try {
			const cached = districtMapCache.current[districtName]
			const { map_names: names = [], district_number: distNum = "" } = cached
				|| await axios.post(FARM_DISTRICT_MAP_NAME_URL, {
					login_id: Token.login_id, client_id: Token.client_id, district_name: districtName,
				}).then(({ data }) => {
					districtMapCache.current[districtName] = data
					return data
				})
			setMapOptions((m) => ({ ...m, [parcelId]: names }))
			setDistrictNumbers((d) => ({ ...d, [parcelId]: distNum }))
			const first = names[0] || ""
			setMapNames((m) => ({ ...m, [parcelId]: first }))
			parcelChange(parcelId, "map_code", first)
			parcelChange(parcelId, "parcel_id", "")
			if (first) setTimeout(() => {
				const input = document.getElementById(`map_parcel_${parcelId}`)
				if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length) }
			}, 0)
		} catch (err) {
			setMapOptions((m) => ({ ...m, [parcelId]: [] }))
			setMapNames((m) => ({ ...m, [parcelId]: "" }))
			setDistrictNumbers((d) => ({ ...d, [parcelId]: "" }))
			setParcelError(`Map name lookup failed for "${districtName}": ${err.response?.data?.detail || err.message}`)
		}
	}

	function _parcelPrefix(parcelId) {
		const dn = districtNumbers[parcelId] || ""
		const mn = mapNames[parcelId] || ""
		if (dn && mn) return dn + "-" + mn + "-"
		if (mn) return mn + "-"
		return ""
	}

	const handleOpFormPhoneChange = (e) => {
		const isDeleting = e.target.value.length < (opForm.phone || "").length
		const result = valid_PhoneNumber(e.target.value, isDeleting)
		setOpForm((p) => ({ ...p, phone: result[2] }))
	}

	function handleParcelIdChange(parcelId, val) {
		const dn = districtNumbers[parcelId] || ""
		const row = parcels.find((p) => p.id === parcelId)
		const mc = row?.map_code || ""
		const prefixLen = (dn ? dn.length + 1 : 0) + (mc ? mc.length + 1 : 0)
		// Keep digits and a single user-typed dash that delimits the two 4-digit sets.
		const suffix = val.slice(prefixLen).replace(/[^\d-]/g, "")
		const dash = suffix.indexOf("-")
		let formatted
		if (dash >= 0) {
			const a = suffix.slice(0, dash).replace(/\D/g, "").slice(0, 4)
			const b = suffix.slice(dash + 1).replace(/\D/g, "").slice(0, 4)
			formatted = a + "-" + b
		} else {
			const digits = suffix.replace(/\D/g, "")
			formatted = digits.slice(0, 4)
			if (digits.length > 4) formatted += "-" + digits.slice(4, 8)
		}
		parcelChange(parcelId, "parcel_id", formatted)
	}

	// On blur, zero-fill BOTH 4-digit sets. A first set with no dash fills the
	// second set with 0000 (e.g. "1234" -> "1234-0000", "12-5" -> "0012-0005").
	function handleParcelIdBlur(parcelId) {
		const row = parcels.find((p) => p.id === parcelId)
		if (!row || !row.parcel_id) return
		const pid = row.parcel_id
		let a, b
		if (pid.includes("-")) {
			const parts = pid.split("-")
			a = (parts[0] || "").replace(/\D/g, "")
			b = (parts[1] || "").replace(/\D/g, "")
		} else {
			const d = pid.replace(/\D/g, "")
			a = d.slice(0, 4); b = d.slice(4, 8)
		}
		if (!a && !b) return
		const pad = (s) => (s ? s.padStart(4, "0").slice(-4) : "0000")
		parcelChange(parcelId, "parcel_id", pad(a) + "-" + pad(b))
	}

	function isParcelIdValid(parcelId, parcel_id) {
		if (!parcel_id) return true
		return /^\d{4}-\d{4}$/.test(parcel_id)
	}

	function parcelHasAcreage(row) {
		return ["pasture_acres", "cropland_acres", "woodland_acres", "waste_acres", "other_acres"].some((f) => parseFloat(row[f]) > 0)
	}

	function fullParcelDisplay(parcelId, row) {
		const dn = districtNumbers[parcelId] || ""
		const mc = row.map_code || ""
		const pid = row.parcel_id || ""
		const prefix = [dn, mc].filter(Boolean).join("-")
		return prefix ? prefix + "-" + pid : pid
	}

	function toggleSection(name) { setActiveSection((s) => s === name ? null : name) }

	async function handleRollForward() {
		if (!owner) return
		await doRollForward(false)
	}

	async function handleRollForwardConfirmed() {
		setShowRollForwardConfirm(false)
		await doRollForward(true)
	}

	async function doRollForward(overwrite) {
		setRollForwardStatus("submitting")
		setRollForwardError("")
		try {
			const { data } = await axios.post(FARM_ROLL_FORWARD_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				owner_id: owner.id,
				fiscal_year: searchFiscalYear,
				overwrite,
			})
			setRollForwardStatus("success")
			setOfficialGranted("Pending"); setOfficialBy("")
			// Target year is authoritative from the backend (past years roll to current FY).
			setRolledForwardYear(data.fiscal_year)
			setSearchFiscalYear(data.fiscal_year)
			setTimeout(() => setRollForwardStatus(""), 2000)
		} catch (err) {
			if (err.response?.status === 409) {
				setRollForwardStatus("")
				setShowRollForwardConfirm(true)
			} else {
				setRollForwardError(err.response?.data?.detail || err.message)
				setRollForwardStatus("")
			}
		}
	}

	function handleDelete() {
		if (!owner) return
		setShowDeleteConfirm(true)
	}

	async function handleDeleteConfirmed() {
		setShowDeleteConfirm(false)
		setDeleteStatus("submitting")
		setDeleteError("")
		try {
			await axios.post(FARM_APPLICATION_DELETE_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				owner_id: owner.id,
				fiscal_year: searchFiscalYear,
			})
			handleClearPage()
			setDeleteStatus("success")
			setTimeout(() => setDeleteStatus(""), 2000)
		} catch (err) {
			setDeleteError(err.response?.data?.detail || err.message)
			setDeleteStatus("")
		}
	}

	function handleClearPage() {
		setSearchValue("")
		setSearchResults([])
		setSearchStatus("")
		setSearchError("")
		setOwner(null)
		setOwnerForm({})
		setOwnerError("")
		setOwnerStatus("")
		setApplications([])
		setActiveSection(null)
		setOpForm(blankOpForm)
		setOpFormStatus("")
		setOpFormError("")
		setParcels([])
		setMapNames({})
		setMapOptions({})
		setDistrictNumbers({})
		setSectionsCache(null)
		setCropRows(DEFAULT_CROP_ROWS)
		setOtherLivestock(DEFAULT_OTHER_LIVESTOCK)
		setHay({ bales: 0, lbs_per_bale: 0, rolls: 0, lbs_per_roll: 0 })
		setTreeRows([])
		setSapTreeRows([])
		setLivestock({ beef_cattle: 0, dairy_cattle: 0, sheep_lambs: 0, goats_kids: 0, hogs_pigs: 0, equine: 0, hens_pullets: 0, broilers: 0, turkeys: 0, poultry_owner: "", bee_colonies: 0, honey_lbs: 0, nucs_sold: 0, queens_sold: 0 })
		setCropValue("");      setProduceValue("");   setLivestockValue("");  setTotalValue("")
		setAgriTourism(false)
		setProdA(false);       setProdADetail("");     setProdB(false);       setProdBYear("")
		setProdC(false);       setProdCCrop("")
		setProdD(false)
		setQ2aManual(null)
		setNonFarmIncome("");  setFarmIncomeOverride(null)
		setQ3Yes(false);       setQ3No(false)
		setQ4Yes(false);       setQ4No(false)
		setOfficialGranted("Pending"); setOfficialBy("")
		setAcresOwned(0);      setAcresRentedFrom(0); setAcresRentedTo(0);   setAcresTotalFarm(0)
		setRollForwardStatus("")
		setRollForwardError("")
		setDeleteStatus("")
		setDeleteError("")
		setShowDeleteConfirm(false)
		setIsRolledForward(false)
		setRolledForwardYear(null)
		setSectionsReviewed({})
		setShowReviewComplete(false)
		reviewCompleteShown.current = false
		setDotSaved(BLANK_DOTS)
	}

	async function handleSearch(e) {
		e.preventDefault()
		if (!searchValue.trim()) { setSearchError("Enter at least one character to search."); return }
		handleClearPage()
		setSearchError("")
		setSearchStatus("loading")

		try {
			const { data } = await axios.post(FARM_OWNER_SEARCH_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				search_value: searchValue,
				fiscal_year: searchFiscalYear,
				pending_only: searchPendingOnly,
			})
			if (data.length === 1) {
				setSearchResults([])
				setSearchStatus("ok")
				await handleSelectOwner(data[0])
			} else {
				setSearchResults(data)
				if (data.length === 0) {
					setSearchStatus("empty")
				} else {
					setSearchStatus("ok")
				}
			}
		} catch (err) {
			if (err.response?.status === 404) {
				setSearchResults([])
				setSearchStatus("empty")
			} else {
				setSearchError(err.response?.data?.detail || err.message)
				setSearchStatus("")
			}
		}
	}

	async function loadApplicationForOwner(ownerRow, fy) {
		const payload = { login_id: Token.login_id, client_id: Token.client_id, owner_id: ownerRow.id, fiscal_year: fy }
		let _parcelRolledFrom = null
		let _parcelReviewed = {}
		try {
			const { data: apps } = await axios.post(FARM_APPLICATION_LOAD_URL, payload)
			setApplications(apps)
			const latest = apps[0] ?? null
			if (!latest) {
				setOpForm({ ...blankOpForm, fiscal_year: fy })
				setActiveSection(null)
				setOpFormStatus("")
				setOpFormError("")
				setSectionsCache(null)
				setCropRows(DEFAULT_CROP_ROWS)
				setOtherLivestock(DEFAULT_OTHER_LIVESTOCK)
				setHay({ bales: 0, lbs_per_bale: 0, rolls: 0, lbs_per_roll: 0 })
				setTreeRows([])
				setSapTreeRows([])
				setTreeSapRows([])
				setLivestock({ beef_cattle: 0, dairy_cattle: 0, sheep_lambs: 0, goats_kids: 0, hogs_pigs: 0, equine: 0, hens_pullets: 0, broilers: 0, turkeys: 0, poultry_owner: "", bee_colonies: 0, honey_lbs: 0, nucs_sold: 0, queens_sold: 0 })
				setCropValue(""); setProduceValue(""); setLivestockValue(""); setTotalValue("")
				setAgriTourism(false)
				setProdA(false); setProdADetail(""); setProdB(false); setProdBYear("")
				setProdC(false); setProdCCrop(""); setProdD(false)
				setQ2aManual(null)
				setNonFarmIncome(""); setFarmIncomeOverride(null)
				setQ3Yes(false); setQ3No(false)
				setQ4Yes(false); setQ4No(false)
				setOfficialGranted("Pending"); setOfficialBy("")
				setDotSaved(BLANK_DOTS)
			}
			if (latest) loadSections(latest.id, latest.rolled_from_year ?? null, latest.sections_reviewed || {})
			if (latest) {
				setAcresOwned(latest.acres_owned || 0)
				setAcresRentedFrom(latest.acres_rented_from || 0)
				setAcresRentedTo(latest.acres_rented_to || 0)
				setAcresTotalFarm((latest.acres_owned || 0) + (latest.acres_rented_from || 0) - (latest.acres_rented_to || 0))
				const opFields = {
					operator_name:         latest.operator_name || "",
					operator_number_addr1: latest.operator_number_addr1 || "",
					operator_street_addr1: latest.operator_street_addr1 || "",
					operator_addr2:        latest.operator_addr2 || "",
					operator_city:         latest.operator_city || "",
					operator_state:        latest.operator_state || "",
					operator_zip:          latest.operator_zip || "",
					operator_phone:        latest.operator_phone || "",
					operator_email:        latest.operator_email || "",
				}
				setOwner((o) => ({ ...o, ...opFields }))
				setOwnerForm((f) => ({ ...f, ...opFields }))
				const isOwnerOp = latest.operator_is_owner
				const currentOpValues = {
					fiscal_year:    latest.fiscal_year,
					primary_county: latest.primary_county ?? false,
					veteran:        latest.veteran ?? false,
					status:         latest.status || "Pending",
					operator_name:  isOwnerOp ? (ownerRow.owner_name || "") : (latest.operator_name || ""),
					email:          isOwnerOp ? ownerRow.email          : (latest.operator_email        || ""),
					phone:          isOwnerOp ? ownerRow.phone          : (latest.operator_phone        || ""),
					number_addr1:   isOwnerOp ? ownerRow.number_addr1   : (latest.operator_number_addr1 || ""),
					street_addr1:   isOwnerOp ? ownerRow.street_addr1   : (latest.operator_street_addr1 || ""),
					addr2:          isOwnerOp ? ownerRow.addr2          : (latest.operator_addr2        || ""),
					city_name:      isOwnerOp ? ownerRow.city_name      : (latest.operator_city         || ""),
					state_code:     isOwnerOp ? ownerRow.state_code     : (latest.operator_state        || "WV"),
					zip:            isOwnerOp ? ownerRow.zip            : (latest.operator_zip          || ""),
				}
				setOpForm(currentOpValues)
				setOfficialGranted(latest.official_granted || "Pending")
				setOfficialBy(latest.official_by || "")
				const appRolledFrom = latest.rolled_from_year ?? null
				const appReviewed = latest.sections_reviewed || {}
				_parcelRolledFrom = appRolledFrom
				_parcelReviewed = appReviewed
				setIsRolledForward(appRolledFrom !== null)
				setSectionsReviewed(appReviewed)
				reviewCompleteShown.current = false
				setLoadDot({
					owner: true,
					operator: !!(latest.operator_name || latest.operator_phone || latest.operator_email),
					acreage: (latest.acres_owned || 0) > 0 || (latest.acres_rented_from || 0) > 0,
				}, appRolledFrom, appReviewed)
			} else {
				setIsRolledForward(false)
				setSectionsReviewed({})
				setAcresOwned(0); setAcresRentedFrom(0); setAcresRentedTo(0); setAcresTotalFarm(0)
			}
		} catch (err) { setApplications([]); setOwnerError("Application load failed: " + (err.response?.data?.detail || err.message)) }
		try {
			const { data: pars } = await axios.post(FARM_PARCEL_LOAD_URL, { login_id: Token.login_id, client_id: Token.client_id, owner_id: ownerRow.id, fiscal_year: fy })
			setParcels(pars)
			setLoadDot({ parcels: pars.length > 0 }, _parcelRolledFrom, _parcelReviewed)
		} catch { setParcels([]) }
	}

	useEffect(() => {
		if (owner) loadApplicationForOwner(owner, searchFiscalYear).then(() => {
			setRolledForwardYear(null)
		})
	}, [searchFiscalYear])

	// Cap the search-results list so it scrolls, ending ~30px above the fixed footer.
	useEffect(() => {
		if (searchStatus !== "ok" || owner) return
		const recalc = () => {
			const el = searchResultsRef.current
			if (!el) return
			const footer = document.querySelector(".Footer")
			const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight
			setResultsMaxHeight(Math.max(120, footerTop - el.getBoundingClientRect().top - 30))
		}
		recalc()
		window.addEventListener("resize", recalc)
		return () => window.removeEventListener("resize", recalc)
	}, [searchStatus, owner, searchResults])

	useEffect(() => {
		if (!isRolledForward) return
		if (reviewCompleteShown.current) return
		const vals = Object.values(dotSaved)
		if (vals.some((v) => v === "pending")) return
		if (!vals.some((v) => v === true)) return
		reviewCompleteShown.current = true
		setShowReviewComplete(true)
		setTimeout(() => setShowReviewComplete(false), 3000)
	}, [dotSaved, isRolledForward])

	async function handleSelectOwner(row) {
		setOwner(row)
		setOwnerForm(row)
		setOwnerError("")
		setOwnerStatus("")

		setActiveSection("owner")
		setMapNames({})
		setMapOptions({})
		setDistrictNumbers({})
		setSectionsCache(null)
		setCropRows(DEFAULT_CROP_ROWS)
		setOtherLivestock(DEFAULT_OTHER_LIVESTOCK)
		setHay({ bales: 0, lbs_per_bale: 0, rolls: 0, lbs_per_roll: 0 })
		setTreeRows([]); setSapTreeRows([])
		// When the search result already contains application data (fiscal_year search),
		// populate opForm directly and skip the separate application load call.
		let selRolledFrom = null
		let selReviewed = {}
		if (row.application_id) {
			const isOwnerOp = row.operator_is_owner
			setApplications([{ id: row.application_id, fiscal_year: row.fiscal_year, status: row.status }])
			setAcresOwned(row.acres_owned || 0)
			setAcresRentedFrom(row.acres_rented_from || 0)
			setAcresRentedTo(row.acres_rented_to || 0)
			setAcresTotalFarm((row.acres_owned || 0) + (row.acres_rented_from || 0) - (row.acres_rented_to || 0))
			const opValues = {
				fiscal_year:    row.fiscal_year,
				primary_county: row.primary_county ?? false,
				veteran:        row.veteran ?? false,
				status:         row.status || "Pending",
				operator_name:  isOwnerOp ? (row.owner_name || "") : (row.operator_name || ""),
				email:          isOwnerOp ? row.email         : (row.operator_email        || ""),
				phone:          isOwnerOp ? row.phone         : (row.operator_phone        || ""),
				number_addr1:   isOwnerOp ? row.number_addr1  : (row.operator_number_addr1 || ""),
				street_addr1:   isOwnerOp ? row.street_addr1  : (row.operator_street_addr1 || ""),
				addr2:          isOwnerOp ? row.addr2         : (row.operator_addr2        || ""),
				city_name:      isOwnerOp ? row.city_name     : (row.operator_city         || ""),
				state_code:     isOwnerOp ? row.state_code    : (row.operator_state        || "WV"),
				zip:            isOwnerOp ? row.zip           : (row.operator_zip          || ""),
			}
			setOpForm(opValues)
				setOfficialGranted(row.status || "Pending")
				setOfficialBy((row.status === "Granted" || row.status === "Denied") ? (Token.contact_name || "") : "")
			selRolledFrom = row.rolled_from_year ?? null
			selReviewed = row.sections_reviewed || {}
			setIsRolledForward(selRolledFrom !== null)
			setSectionsReviewed(selReviewed)
			reviewCompleteShown.current = false
			setLoadDot({
				owner: true,
				operator: !!(row.operator_name || row.operator_phone || row.operator_email),
				acreage: (row.acres_owned || 0) > 0 || (row.acres_rented_from || 0) > 0,
			}, selRolledFrom, selReviewed)
			loadSections(row.application_id, selRolledFrom, selReviewed)
		} else {
			await loadApplicationForOwner(row, searchFiscalYear)
		}
		try {
			const { data: pars } = await axios.post(FARM_PARCEL_LOAD_URL, { login_id: Token.login_id, client_id: Token.client_id, owner_id: row.id, fiscal_year: searchFiscalYear })
			setParcels(pars)
			setLoadDot({ parcels: pars.length > 0 }, selRolledFrom, selReviewed)
			// Reconstruct map dropdowns for loaded parcels
			const uniqueDistricts = [...new Set(pars.filter((p) => p.tax_district).map((p) => p.tax_district))]
			const districtData = {}
			await Promise.all(uniqueDistricts.map(async (dn) => {
				try {
					const cached = districtMapCache.current[dn]
					const d = cached || await axios.post(FARM_DISTRICT_MAP_NAME_URL, {
						login_id: Token.login_id, client_id: Token.client_id, district_name: dn,
					}).then(({ data }) => { districtMapCache.current[dn] = data; return data })
					districtData[dn] = { map_names: d.map_names || [], district_number: d.district_number || "" }
				} catch { districtData[dn] = { map_names: [], district_number: "" } }
			}))
			const newMapOptions = {}, newMapNames = {}, newDistrictNumbers = {}
			for (const p of pars) {
				const dd = p.tax_district && districtData[p.tax_district]
				if (!dd) continue
				const { map_names, district_number } = dd
				newMapOptions[p.id] = map_names
				newDistrictNumbers[p.id] = district_number
				newMapNames[p.id] = p.map_code || ""
			}
			setMapOptions(newMapOptions)
			setMapNames(newMapNames)
			setDistrictNumbers(newDistrictNumbers)
		} catch { setParcels([]) }
	}

	async function loadSections(applicationId, rolledFromYear = null, reviewed = {}) {
		if (!applicationId) return
		try {
			const { data } = await axios.post(FARM_APP_SECTIONS_LOAD_URL, {
				login_id: Token.login_id, client_id: Token.client_id, application_id: applicationId,
			})
			setSectionsCache(data)
			// Crops — row-based; standard 6 always present, extras appended
			const loadedCrops = data.crops || []
			const standardRows = STANDARD_CROPS.map(({ name }, i) => {
				const found = loadedCrops.find((r) => r.name === name)
				return { id: i + 1, name, acres: found ? found.acres : 0, sqft: found?.sqft ?? 0, vegetable: found?.vegetable ?? false, high_tunnel: found?.hightunnel ?? false }
			})
			const specialtyRows = loadedCrops
				.filter((r) => !STANDARD_CROP_NAMES.has(r.name))
				.map((r, i) => ({ id: 1000 + i, name: r.name, acres: r.acres, sqft: r.sqft ?? 0, vegetable: r.vegetable ?? false, high_tunnel: r.hightunnel ?? false }))
			setCropRows([...standardRows, ...specialtyRows])
			const h = data.hay || {}
			setHay({ bales: h.bales || 0, lbs_per_bale: h.bale_weight || 0, rolls: h.rolls || 0, lbs_per_roll: h.roll_weight || 0 })
			// Trees
			setTreeRows((data.trees || []).map((r, i) => ({
				id: i + 1, tree_type: r.tree_name, tree_count: r.tree_count, acres: r.tree_acres,
			})))
			setSapTreeRows((data.sap_trees || []).map((r, i) => ({
				id: i + 1, tree_type: r.tree_type, tree_count: r.tap_count, gallons: r.gallons,
			})))
			// Livestock — row-based: rows matching OTHER_LIVESTOCK_TYPES go to otherLivestock;
			// rows matching LIVESTOCK_MAP names go to the fixed livestock fields
			const lsMap = {}
			const savedOtherMap = {}
			;(data.livestock || []).forEach((r) => {
				const lmEntry = LIVESTOCK_MAP.find((m) => m.name === r.name)
				if (lmEntry) lsMap[lmEntry.field] = r.count
				else savedOtherMap[r.name] = r.count
			})
			const poultryRows = data.poultry || []
			const pMap = {}
			poultryRows.forEach((r) => { pMap[r.name] = { count: r.count, owner: r.poutry_owner } })
			const poultryOwner = poultryRows[0]?.poutry_owner || ""
			const bees = data.bees || {}
			setLivestock({
				beef_cattle:  lsMap.beef_cattle  || 0, dairy_cattle: lsMap.dairy_cattle || 0,
				sheep_lambs:  lsMap.sheep_lambs  || 0, goats_kids:   lsMap.goats_kids   || 0,
				hogs_pigs:    lsMap.hogs_pigs    || 0, equine:       lsMap.equine       || 0,
				hens_pullets: pMap["Hens & Pullets"]?.count || 0,
				broilers:     pMap["Broilers"]?.count       || 0,
				turkeys:      pMap["Turkeys"]?.count        || 0,
				poultry_owner: poultryOwner,
				bee_colonies: bees.bee_colonies  || 0, honey_lbs:    bees.honey_lbs     || 0,
				nucs_sold:    bees.nucs_sold     || 0, queens_sold:  bees.queens_sold   || 0,
			})
			// Restore otherLivestock: default 6 rows with saved counts, plus any extra saved rows
			const restoredOther = DEFAULT_OTHER_LIVESTOCK.map((d) => ({
				...d, count: savedOtherMap[d.animal_type] ?? d.count,
			}))
			const extraOther = Object.entries(savedOtherMap)
				.filter(([name]) => !DEFAULT_OTHER_LIVESTOCK.some((d) => d.animal_type === name))
				.map(([name, count], i) => ({ id: 1000 + i, animal_type: name, count }))
			setOtherLivestock([...restoredOther, ...extraOther])
			// Light-green Livestock button if any livestock/poultry/bees data is non-zero
			const hasLivestock = Object.values(savedOtherMap).some((c) => Number(c) > 0) ||
				poultryRows.some((r) => r.count > 0) ||
				Object.values(bees).some((v) => Number(v) > 0)
			// Production values
			const fmt = (n) => n > 0 ? Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""
			const pr = data.production || {}
			setCropValue(fmt(pr.crop_value || 0))
			setProduceValue(fmt(pr.produce_value || 0))
			setLivestockValue(fmt(pr.livestock_value || 0))
			setAgriTourism(pr.agri_tourism || false)
			// Qualification values
			const q = data.qualification || {}
			// Tri-state. The old test was `!== undefined`, which a NOT NULL column
			// could never fail, so every unanswered application showed a firm No.
			setQ2aManual(q.income_pct_agricultural ?? null)
			setNonFarmIncome(q.non_farm_income > 0 ? Number(q.non_farm_income).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "")
			// A saved farm income that differs from the production total was typed
			// by hand and is restored as an override; one that matches leaves the
			// field following the total. Zero counts as a hand-entered figure - it
			// is exactly the case Hancock raised, an heir with production but no
			// income - so it must not be mistaken for "nothing saved".
			{
				const savedIncome = Number(q.farm_income) || 0
				const prodTotal = (Number(pr.crop_value) || 0) + (Number(pr.produce_value) || 0) +
					(Number(pr.livestock_value) || 0)
				setFarmIncomeOverride(savedIncome !== prodTotal ? money(savedIncome) : null)
			}
			setQ3Yes(q.corporation_farming === true)
			setQ3No(q.corporation_farming === false)
			setQ4Yes(q.conservation_easement === true)
			setQ4No(q.conservation_easement === false)
			setProdA(q.checkbox_value === "A")
			setProdB(q.checkbox_value === "B")
			setProdBYear(q.b_year || "")
			setProdC(q.checkbox_value === "C")
			setProdCCrop(q.c_crop || "")
			setProdD(q.checkbox_value === "D")
			setProdADetail(q.a_detail || "")
			// Dot indicators — reflect previously saved data
			setLoadDot({
				crops:          loadedCrops.some((r) => r.acres > 0),
				hay:            (h.bales || 0) > 0 || (h.rolls || 0) > 0 || (h.bale_weight || 0) > 0 || (h.roll_weight || 0) > 0,
				trees:          (data.trees || []).some((r) => r.tree_count > 0 || r.tree_acres > 0),
				sapTrees:       (data.sap_trees || []).some((r) => r.tap_count > 0 || r.gallons > 0),
				livestockRows:  Object.values(savedOtherMap).some((c) => Number(c) > 0),
				poultry:        poultryRows.some((r) => r.count > 0),
				bees:           (bees.bee_colonies || 0) > 0,
				prodValues:     (pr.crop_value || 0) > 0 || (pr.produce_value || 0) > 0 || (pr.livestock_value || 0) > 0,
				qualification:  !!(data.qualification && (
					// A recorded No counts as answered; only NULL is unanswered.
					data.qualification.income_pct_agricultural != null ||
					data.qualification.corporation_farming != null ||
					data.qualification.conservation_easement != null ||
					data.qualification.checkbox_value ||
					(data.qualification.non_farm_income || 0) > 0 ||
					(data.qualification.farm_income || 0) > 0
				)),
			}, rolledFromYear, reviewed)
		} catch {
			setCropRows(DEFAULT_CROP_ROWS); setHay({ bales: 0, lbs_per_bale: 0, rolls: 0, lbs_per_roll: 0 })
			setTreeRows([]); setSapTreeRows([])
		}
	}

	async function markSectionReviewed(section) {
		const app = applications[0]
		// Recorded for every application, not just rolled-forward ones: it is what
		// keeps a section's dot lit after a deliberate save of all zeros, which
		// carries no data for the dot to be derived from on the next load.
		if (!app) return
		try {
			await axios.post(FARM_SECTION_REVIEWED_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				application_id: app.id,
				section,
			})
			setSectionsReviewed((prev) => ({ ...prev, [section]: true }))
		} catch { /* non-critical — dot already green, DB update is best-effort */ }
	}

	async function handleSaveTrees(source = "trees") {
		setTreesSaveSource(source)
		const app = applications[0]
		if (!app) { setTreesError("No application on file — save Farm Operation Description first."); return }
		setTreesStatus("submitting")
		setTreesError("")
		try {
			const trees = treeRows
				.filter((r) => r.tree_type && r.tree_type !== "__custom__")
				.map((r) => ({
					tree_name:  r.tree_type,
					tree_count: parseFloat(r.tree_count) || 0,
					tree_acres: parseFloat(r.acres) || 0,
				}))
			const sap_trees = sapTreeRows
				.filter((r) => r.tree_type && r.tree_type !== "__custom__")
				.map((r) => ({
					tree_type: r.tree_type,
					tap_count: parseInt(r.tree_count) || 0,
					gallons:   parseFloat(r.gallons) || 0,
				}))
			// Send only the sections this screen edits; omit the rest so the bulk
			// save leaves them untouched (the backend treats omitted sections as None).
			await axios.post(FARM_APP_SECTIONS_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id, application_id: app.id,
				trees, sap_trees,
			})
			setTreesStatus("success")
			if (source === "sap") setDot({ sapTrees: true }); else setDot({ trees: true })
			markSectionReviewed(source === "sap" ? "sapTrees" : "trees")
			setTimeout(() => setTreesStatus(""), 2000)
		} catch (err) { setTreesStatus(""); setTreesError(err.response?.data?.detail || err.message) }
	}

	async function handleSaveProduction() {
		const app = applications[0]
		if (!app) { setProdValueError("No application on file — save Farm Operation Description first."); return }
		setProdValueStatus("submitting")
		setProdValueError("")
		setQualStatus("")
		try {
			await axios.post(FARM_PRODUCTION_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id,
				application_id: app.id,
				crop_value:       parseFloat(cropValue.replace(/,/g,""))      || 0,
				produce_value:    parseFloat(produceValue.replace(/,/g,""))   || 0,
				livestock_value:  parseFloat(livestockValue.replace(/,/g,"")) || 0,
				total_value:      (parseFloat(cropValue.replace(/,/g,"")) || 0) + (parseFloat(produceValue.replace(/,/g,"")) || 0) + (parseFloat(livestockValue.replace(/,/g,"")) || 0),
				agri_tourism:     agriTourism,
			})
			setProdValueStatus("success")
			setDot({ prodValues: true })
			markSectionReviewed("prodValues")
			setTimeout(() => setProdValueStatus(""), 2000)
		} catch (err) { setProdValueStatus(""); setProdValueError(err.response?.data?.detail || err.message) }
	}

	async function handleSaveQualification() {
		const app = applications[0]
		if (!app) { setQualError("No application on file — save Farm Operation Description first."); return }
		setQualStatus("submitting")
		setQualError("")
		setProdValueStatus("")
		try {
			const lowProd = prodA ? "A" : prodB ? "B" : prodC ? "C" : prodD ? "D" : null
			// Tri-state: neither box ticked saves as NULL, not as No.
			const answer = (yes, no) => yes ? true : no ? false : null
			await axios.post(FARM_QUALIFICATION_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id,
				application_id: app.id,
				income_pct_agricultural: answer(q2aYes, q2aNo),
				non_farm_income: parseFloat((nonFarmIncome || "").replace(/,/g, "")) || 0,
				farm_income:     parseFloat((farmIncome || "").replace(/,/g, "")) || 0,
				corporation_farming:   answer(q3Yes, q3No),
				conservation_easement: answer(q4Yes, q4No),
				checkbox_value: lowProd,
				b_year: prodBYear || null,
				c_crop: prodCCrop || null,
				a_detail: prodADetail || null,
				official_granted: officialGranted || null,
				official_by: officialBy || null,
			})
			setQualStatus("success")
			// Status mirrors the OFFICIAL USE decision once qualification is saved.
			setOpForm((p) => ({ ...p, status: officialGranted || "Pending" }))
			setDot({ qualification: true })
			markSectionReviewed("qualification")
			setTimeout(() => setQualStatus(""), 2000)
		} catch (err) { setQualStatus(""); setQualError(err.response?.data?.detail || err.message) }
	}

	async function handleSaveBees() {
		const app = applications[0]
		if (!app) { setBeesError("No application on file — save Farm Operation Description first."); return }
		setBeesStatus("submitting")
		setBeesError("")
		try {
			await axios.post(FARM_BEES_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id,
				application_id: app.id,
				bee_colonies: parseInt(livestock.bee_colonies) || 0,
				honey_lbs:    parseFloat(livestock.honey_lbs)  || 0,
				nucs_sold:    parseInt(livestock.nucs_sold)    || 0,
				queens_sold:  parseInt(livestock.queens_sold)  || 0,
			})
			setBeesStatus("success")
			setDot({ bees: true })
			markSectionReviewed("bees")
			setTimeout(() => setBeesStatus(""), 2000)
		} catch (err) { setBeesStatus(""); setBeesError(err.response?.data?.detail || err.message) }
	}

	async function handleSavePoultry() {
		const app = applications[0]
		if (!app) { setPoultryError("No application on file — save Farm Operation Description first."); return }
		setPoultryStatus("submitting")
		setPoultryError("")
		try {
			const owner = livestock.poultry_owner || null
			await axios.post(FARM_POULTRY_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id,
				application_id: app.id,
				poultry: [
					{ name: "Hens & Pullets", count: parseInt(livestock.hens_pullets) || 0, poutry_owner: owner },
					{ name: "Broilers",       count: parseInt(livestock.broilers)     || 0, poutry_owner: owner },
					{ name: "Turkeys",        count: parseInt(livestock.turkeys)      || 0, poutry_owner: owner },
				],
			})
			setPoultryStatus("success")
			setDot({ poultry: true })
			markSectionReviewed("poultry")
			setTimeout(() => setPoultryStatus(""), 2000)
		} catch (err) { setPoultryStatus(""); setPoultryError(err.response?.data?.detail || err.message) }
	}

	async function handleSaveLivestockRows() {
		const app = applications[0]
		if (!app) { setLivestockRowsError("No application on file — save Farm Operation Description first."); return }
		setLivestockRowsStatus("submitting")
		setLivestockRowsError("")
		try {
			await axios.post(FARM_LIVESTOCK_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id,
				application_id: app.id,
				livestock: otherLivestock.map((r) => ({ name: r.animal_type, count: parseInt(r.count) || 0 })),
			})
			setLivestockRowsStatus("success")
			setDot({ livestockRows: true })
			markSectionReviewed("livestockRows")
			setTimeout(() => setLivestockRowsStatus(""), 2000)
		} catch (err) { setLivestockRowsStatus(""); setLivestockRowsError(err.response?.data?.detail || err.message) }
	}

	async function handleSaveLivestock() {
		const app = applications[0]
		if (!app) { setLivestockError("No application on file — save Farm Operation Description first."); return }
		setLivestockStatus("submitting")
		setLivestockError("")
		try {
			const livestockRows = LIVESTOCK_MAP.map(({ name, field }) => ({
				name, count: parseInt(livestock[field]) || 0,
			}))
			const beesPayload = {
				bee_colonies: parseInt(livestock.bee_colonies) || 0,
				honey_lbs:    parseFloat(livestock.honey_lbs)  || 0,
				nucs_sold:    parseInt(livestock.nucs_sold)    || 0,
				queens_sold:  parseInt(livestock.queens_sold)  || 0,
			}
			// Send only the sections this screen edits; omit the rest so the bulk
			// save leaves them untouched (the backend treats omitted sections as None).
			await axios.post(FARM_APP_SECTIONS_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id, application_id: app.id,
				livestock: livestockRows, bees: beesPayload,
			})
			setLivestockStatus("success")
			setTimeout(() => setLivestockStatus(""), 2000)
		} catch (err) { setLivestockStatus(""); setLivestockError(err.response?.data?.detail || err.message) }
	}

	async function handleSaveHayProduction() {
		const app = applications[0]
		if (!app) { setHayError("No application on file — save Farm Operation Description first."); return }
		setHayStatus("submitting")
		setHayError("")
		try {
			await axios.post(FARM_HAY_PRODUCTION_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id,
				application_id: app.id,
				bales:       parseFloat(hay.bales)        || 0,
				bale_weight: parseFloat(hay.lbs_per_bale) || 0,
				rolls:       parseFloat(hay.rolls)        || 0,
				roll_weight: parseFloat(hay.lbs_per_roll) || 0,
			})
			setHayStatus("success")
			setDot({ hay: true })
			markSectionReviewed("hay")
			setTimeout(() => setHayStatus(""), 2000)
		} catch (err) { setHayStatus(""); setHayError(err.response?.data?.detail || err.message) }
	}

	async function handleSaveCrops() {
		const app = applications[0]
		if (!app) { setCropsError("No application on file — save Farm Operation Description first."); return }
		setCropsStatus("submitting")
		setCropsError("")
		try {
			const standardCrops = STANDARD_CROPS.map((c) => {
				const row = cropRows.find((r) => r.name === c.name)
				return {
					name: c.name,
					acres: parseFloat(row?.acres) || 0,
					sqft: row?.sqft ? parseInt(row.sqft) : null,
					vegetable: row?.vegetable ?? null,
					hightunnel: row?.high_tunnel ?? null,
				}
			})
			const specialtyCrops = cropRows
				.filter((r) => !STANDARD_CROP_NAMES.has(r.name) && r.name && r.name !== "__custom__")
				.map((r) => ({
					name: r.name,
					acres: parseFloat(r.acres) || 0,
					sqft: r.sqft ? parseInt(r.sqft) : null,
					vegetable: r.vegetable ?? null,
					hightunnel: r.high_tunnel ?? null,
				}))
			await axios.post(FARM_CROPS_SAVE_URL, {
				login_id: Token.login_id, client_id: Token.client_id, application_id: app.id,
				crops: [...standardCrops, ...specialtyCrops],
			})
			setCropsStatus("success")
			setDot({ crops: true })
			markSectionReviewed("crops")
			setTimeout(() => setCropsStatus(""), 2000)
		} catch (err) { setCropsStatus(""); setCropsError(err.response?.data?.detail || err.message) }
	}

	function _opPayload(form, fiscalYear) {
		return {
			login_id: Token.login_id,
			client_id: Token.client_id,
			owner_id: owner.id,
			fiscal_year: fiscalYear,
			status: form.status,
			primary_county: form.primary_county,
			veteran: form.veteran,
			operator_name: form.operator_name || null,
			operator_number_addr1: form.number_addr1 || null,
			operator_street_addr1: form.street_addr1 || null,

			operator_addr2: form.addr2 || null,
			operator_city: form.city_name || null,
			operator_state: form.state_code || null,
			operator_zip: form.zip || null,
			operator_phone: form.phone || null,
			operator_email: form.email || null,
		}
	}

	async function handleSaveOpForm() {
		setOpFormStatus("submitting")
		setOpFormError("")
		try {
			await axios.post(FARM_APPLICATION_OPERATOR_SAVE_URL, _opPayload(opForm, searchFiscalYear))
			setOpFormStatus("success")
			setDot({ operator: !!(opForm.operator_name || opForm.phone) })
			markSectionReviewed("operator")
			setTimeout(() => setOpFormStatus(""), 2000)
		} catch (err) { setOpFormStatus(""); setOpFormError(err.response?.data?.detail || err.message) }
	}


	async function handleSaveOpDesc() {
		setOpDescStatus("submitting")
		setOpDescError("")
		const app = applications[0]
		try {
			await axios.post(FARM_APPLICATION_SAVE_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				owner_id: owner.id,
				fiscal_year: searchFiscalYear,
				status:           app?.status           || "Pending",
				veteran:          app?.veteran          ?? false,
				primary_county:   app?.primary_county   ?? true,
				acres_owned:      acresOwned,
				acres_rented_from: acresRentedFrom,
				acres_rented_to:  acresRentedTo,
				operator_name:    app?.operator_name    || null,
				operator_addr2:   app?.operator_addr2   || null,
				operator_city:    app?.operator_city    || null,
				operator_state:   app?.operator_state   || null,
				operator_zip:     app?.operator_zip     || null,
				operator_phone:   app?.operator_phone   || null,
				operator_email:   app?.operator_email   || null,
				prod_a: app?.prod_a ?? false, prod_b: app?.prod_b ?? false,
				prod_b_year: app?.prod_b_year || null,
				prod_c: app?.prod_c ?? false, prod_c_crop: app?.prod_c_crop || null,
				prod_d: app?.prod_d ?? false,
				q2a: app?.q2a || null, non_farm_income: app?.non_farm_income || null,
				farm_income: app?.farm_income || null, q3: app?.q3 || null, q4: app?.q4 || null,
			})
			setOpDescStatus("success")
			setDot({ acreage: true })
			markSectionReviewed("acreage")
			setTimeout(() => setOpDescStatus(""), 2000)
			// Reload applications so subsequent section saves can find applications[0]
			if (owner?.id) {
				try {
					const { data: apps } = await axios.post(FARM_APPLICATION_LOAD_URL, {
						login_id: Token.login_id, client_id: Token.client_id, owner_id: owner.id,
					})
					setApplications(apps)
				} catch { }
			}
		} catch (err) {
			setOpDescStatus("")
			setOpDescError(err.response?.data?.detail || err.message)
		}
	}

	async function handleSaveParcels() {
		setParcelStatus("submitting")
		setParcelError("")
		try {
			await axios.post(FARM_PARCEL_SAVE_ALL_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				owner_id: owner.id,
				fiscal_year: searchFiscalYear,
				parcels,
			})
			setParcelStatus("success")
			setDot({ parcels: true })
			markSectionReviewed("parcels")
			setTimeout(() => setParcelStatus(""), 2000)
		} catch (err) {
			setParcelError(err.response?.data?.detail || err.message)
			setParcelStatus("")
		}
	}

	async function handleOwnerSave() {
		setOwnerStatus("submitting")
		setOwnerError("")
		try {
			const isNewOwner = !owner?.id
			let savedId = owner?.id
			let reloaded
			if (owner?.id) {
				const { data } = await axios.put(FARM_OWNER_EDIT_URL, {
					login_id: Token.login_id,
					client_id: Token.client_id,
					owner_id: owner.id,
					...ownerForm,
				})
				reloaded = data
			} else {
				const { data } = await axios.post(FARM_OWNER_ADD_URL, {
					login_id: Token.login_id,
					client_id: Token.client_id,
					...ownerForm,
				})
				savedId = data.id
				// New owner: load to get Smarty-corrected address
				const { data: loaded } = await axios.post(FARM_OWNER_LOAD_URL, {
					login_id: Token.login_id, client_id: Token.client_id, owner_id: savedId,
				})
				reloaded = loaded
			}
			setOwner((prev) => ({ ...prev, ...reloaded }))
			setOwnerForm(reloaded)
			if (isNewOwner) {
				// New owner — reset every data-bearing field to its initial value
				setApplications([])
				setParcels([]);        setMapNames({});          setMapOptions({});         setDistrictNumbers({})
				setSectionsCache(null)
				setCropRows(DEFAULT_CROP_ROWS)
				setHay({ bales: 0, lbs_per_bale: 0, rolls: 0, lbs_per_roll: 0 })
				setTreeRows([]);       setSapTreeRows([])
				setLivestock({ beef_cattle: 0, dairy_cattle: 0, sheep_lambs: 0, goats_kids: 0, hogs_pigs: 0, equine: 0, hens_pullets: 0, broilers: 0, turkeys: 0, poultry_owner: "", bee_colonies: 0, honey_lbs: 0, nucs_sold: 0, queens_sold: 0 })
				setOtherLivestock(DEFAULT_OTHER_LIVESTOCK)
				setCropValue("");      setProduceValue("");      setLivestockValue("");     setTotalValue("")
				setAgriTourism(false)
				setProdA(false);       setProdB(false);          setProdBYear("")
				setProdC(false);       setProdCCrop("")
				setProdD(false)
				setQ2aManual(null)
				setNonFarmIncome("");  setFarmIncomeOverride(null)
				setQ3Yes(false);       setQ3No(false)
				setQ4Yes(false);       setQ4No(false)
				setOfficialGranted("Pending"); setOfficialBy("")
				setAcresOwned(0);      setAcresRentedFrom(0);    setAcresRentedTo(0);      setAcresTotalFarm(0)
				setOpForm(blankOpForm)
			}
			// Always create/update the application record so section saves can find applications[0]
			const syncedOpForm = reloaded.operator_is_owner
				? {
					...opForm,
					operator_name: reloaded.owner_name  || "",
					number_addr1: reloaded.number_addr1 || "",
					street_addr1: reloaded.street_addr1 || "",
					addr2:        reloaded.addr2        || "",
					city_name:    reloaded.city_name    || "",
					state_code:   reloaded.state_code   || "WV",
					zip:          reloaded.zip          || "",
					email:        reloaded.email        || "",
					phone:        reloaded.phone        || "",
				}
				: opForm
			if (reloaded.operator_is_owner) setOpForm(syncedOpForm)
			const { data: appData } = await axios.post(FARM_APPLICATION_OPERATOR_SAVE_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				owner_id: savedId,
				fiscal_year: searchFiscalYear,
				status: syncedOpForm.status || "Pending",
				primary_county: syncedOpForm.primary_county ?? true,
				veteran: syncedOpForm.veteran ?? false,
				operator_name: syncedOpForm.operator_name || null,
				operator_number_addr1: syncedOpForm.number_addr1 || null,
				operator_street_addr1: syncedOpForm.street_addr1 || null,
				operator_addr2:        syncedOpForm.addr2        || null,
				operator_city:         syncedOpForm.city_name   || null,
				operator_state:        syncedOpForm.state_code  || null,
				operator_zip:          syncedOpForm.zip         || null,
				operator_phone:        syncedOpForm.phone       || null,
				operator_email:        syncedOpForm.email       || null,
			})
			if (appData?.id) {
				setApplications([{ id: appData.id, fiscal_year: searchFiscalYear, status: "Pending", dt_filed: null, dt_certified: null }])
			}
			setDot({ owner: true, operator: !!(syncedOpForm.operator_name || syncedOpForm.phone) })
			markSectionReviewed("owner")
			markSectionReviewed("operator")
			setOwnerMode("view")
			setOwnerStatus("success")
			setTimeout(() => setOwnerStatus(""), 2000)
		} catch (err) {
			setOwnerError(err.response?.data?.detail || err.message)
			setOwnerStatus("")
		}
	}

	const ownerChange = (field, transform) => (e) =>
		setOwnerForm((prev) => ({
			...prev,
			[field]: e.target.type === "checkbox" ? e.target.checked
				: transform ? transform(e.target.value) : e.target.value,
		}))

	// "Owner is Operator": checked -> operator_name = owner_name; unchecked -> clear
	// the whole operator block (it's now a separate party to be entered).
	const handleOwnerOperatorToggle = (e) => {
		const checked = e.target.checked
		setOwnerForm((prev) => ({ ...prev, operator_is_owner: checked }))
		if (checked) {
			setOpForm((p) => ({
				...p,
				operator_name: ownerForm.owner_name || "",
				email:        ownerForm.email || "",
				phone:        ownerForm.phone || "",
				number_addr1: ownerForm.number_addr1 || "",
				street_addr1: ownerForm.street_addr1 || "",
				addr2:        ownerForm.addr2 || "",
				city_name:    ownerForm.city_name || "",
				state_code:   ownerForm.state_code || "WV",
				zip:          ownerForm.zip || "",
			}))
		} else {
			setOpForm((p) => ({
				...p,
				operator_name: "", email: "", phone: "",
				number_addr1: "", street_addr1: "", addr2: "",
				city_name: "", state_code: "", zip: "",
			}))
		}
	}

	function handleEnterAsTab(e) {
		if (e.key !== "Enter" || e.target.tagName.toLowerCase() !== "input" || e.target.type === "checkbox") return
		e.preventDefault()
		const focusable = [...document.querySelectorAll(
			'input:not([disabled]):not([type="checkbox"]), select:not([disabled]), textarea:not([disabled])'
		)].filter((el) => el.offsetParent !== null)
		const idx = focusable.indexOf(e.target)
		if (idx >= 0 && idx < focusable.length - 1) focusable[idx + 1].focus()
	}

	return (
		<main className="Main" onKeyDown={handleEnterAsTab}>

			{/* Search */}
			<fieldset style={{ width: "fit-content", margin: "0 auto" }}>
				<legend>Find Owner</legend>
				<form onSubmit={handleSearch}>
					<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.6rem" }}>
						<label htmlFor="farm_search_fy">Fiscal Year</label>
						<select id="farm_search_fy" value={searchFiscalYear} onChange={(e) => { const yr = Number(e.target.value); handleClearPage(); setSearchFiscalYear(yr) }}>
							{/* 5 prior years (historical/imported applications) + current + next (roll-forward target) */}
							{Array.from({ length: 7 }, (_, i) => CURRENT_FY - 5 + i).map((fy) => (
								<option key={fy} value={fy}>{fyLabel(fy)}</option>
							))}
						</select>
						<label htmlFor="farm_pending_only" style={{ marginLeft: "1rem" }}>Pending</label>
						<input id="farm_pending_only" type="checkbox" checked={searchPendingOnly} onChange={(e) => { setSearchPendingOnly(e.target.checked); setSearchResults([]) }} />
					</div>
					<span style={{ display: "inline-block", marginRight: "15px" }}>
						<label htmlFor="farm_search_value" style={{ display: "block" }}>Search by:</label>
						<input
							id="farm_search_value"
							type="text"
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSearch(e) } }}
							placeholder="Owner Name || *"
							style={{ width: "220px" }}
						/>
					</span>
					<span style={{ display: "inline-flex", alignItems: "flex-start", gap: "0.5rem", marginLeft: "10px", verticalAlign: "top" }}>
						{/* Owner button with progress dots */}
						{(() => {
							const dots = [dotSaved.owner, dotSaved.operator]
							return (
								<div className="farm-section-btn-wrap">
									<button className="saveBtn" type="button" onClick={() => {
										if (!owner) {
											setOwnerForm(blankOwner)
											setOwnerMode("edit")
										}
										toggleSection("owner")
									}}>Owner</button>
									<div className="farm-progress-dots">
										{dots.map((done, i) => <span key={i} className={`farm-progress-dot${done === true ? " done" : done === "pending" ? " pending" : ""}`} />)}
									</div>
								</div>
							)
						})()}
						{/* Land – Crops – Trees – Livestock – Production */}
						{[
							{ key: "land",       label: "Land",       dots: [dotSaved.acreage, dotSaved.parcels] },
							{ key: "crops",      label: "Crops",      dots: [dotSaved.crops, dotSaved.hay] },
							{ key: "trees",      label: "Trees",      dots: [dotSaved.trees, dotSaved.sapTrees] },
							{ key: "livestock",  label: "Livestock",  dots: [dotSaved.livestockRows, dotSaved.poultry, dotSaved.bees] },
							{ key: "production", label: "Production", dots: [dotSaved.prodValues, dotSaved.qualification] },
						].map(({ key, label, dots }) => (
							<div key={key} className="farm-section-btn-wrap">
								<button className="saveBtn" type="button" onClick={() => toggleSection(key)} disabled={!applications[0]}>{label}</button>
								<div className="farm-progress-dots">
									{dots.map((done, i) => <span key={i} className={`farm-progress-dot${done === true ? " done" : done === "pending" ? " pending" : ""}`} />)}
								</div>
							</div>
						))}
					</span>
				</form>

				{owner && (
					<div style={{ marginTop: "0.4rem" }}>
						{isRolledForward && (() => {
							const labels = { owner: "Owner", operator: "Operator", acreage: "Acreage", parcels: "Parcels", crops: "Crops", hay: "Hay", trees: "Trees", sapTrees: "Sap Trees", livestockRows: "Livestock", poultry: "Poultry", bees: "Bees", prodValues: "Production Values", qualification: "Qualification" }
							const pending = Object.keys(dotSaved).filter((k) => dotSaved[k] === "pending").map((k) => labels[k])
							if (pending.length === 0) return null
							return (
								<div style={{ marginBottom: "0.4rem", color: "#daa520", fontWeight: "bold" }}>
									Review pending — save these sections to finish: {pending.join(", ")}
								</div>
							)
						})()}
						<span style={{ marginRight: "1rem" }}>
							{searchFiscalYear <= CURRENT_FY && (() => {
								// Past years roll forward to the current FY; the current FY rolls to next FY.
								const rollTarget = searchFiscalYear < CURRENT_FY ? CURRENT_FY : searchFiscalYear + 1
								return (
							<button
								className="saveBtn"
								type="button"
								onClick={handleRollForward}
								disabled={rollForwardStatus === "submitting"}
								title={`Copy ${fyLabel(searchFiscalYear)} application to ${fyLabel(rollTarget)}`}
							>
								Roll Forward
							</button>
								)
							})()}
							{rollForwardStatus === "success" && <StatusPopup message={`Rolled forward to ${fyLabel(searchFiscalYear)}.`} />}
						{showReviewComplete && <StatusPopup message="Farm Application Review Complete." />}
							{rollForwardError && <ErrorPopup message={rollForwardError} onClose={() => setRollForwardError("")} />}
						</span>
						<span style={{ marginRight: "1rem" }}>
							<button className="saveBtn" type="button" onClick={handleClearPage} title="Preparation for Add New">Clear Page</button>
						</span>
						{[
							["Owner:", owner.owner_name],
							["Operation:", owner.operation_name],
							["Operator:", owner.operator_is_owner ? "Same as Owner" : owner.operator_name],
						].map(([label, value]) => (
							<span key={label} style={{ marginRight: "1rem", color: Theme === 4 ? "#333333" : "white" }}>
								<span style={{ fontWeight: "bold", marginRight: "0.4rem" }}>{label}</span>
								<span>{value}</span>
							</span>
						))}
						<button
							className="saveBtn"
							type="button"
							onClick={handleDelete}
							disabled={deleteStatus === "submitting"}
							style={{ backgroundColor: "#c0392b", color: "white" }}
							title="Delete this fiscal year's farm application"
						>
							Delete Farm Application
						</button>
					</div>
				)}

				{searchStatus === "loading" && <StatusPopup message="Searching..." />}
				{searchStatus === "empty" && <StatusPopup message="No owners found." />}
				{deleteStatus === "success" && <StatusPopup message={`Deleted ${fyLabel(searchFiscalYear)} application.`} />}
				{deleteError && <ErrorPopup message={deleteError} onClose={() => setDeleteError("")} />}
				<ErrorPopup message={searchError} onClose={() => setSearchError("")} />
				{searchStatus === "ok" && !owner && (
					<div ref={searchResultsRef} style={{ marginTop: "0.75rem", overflowX: "auto", overflowY: "auto", maxHeight: resultsMaxHeight ? `${resultsMaxHeight}px` : undefined, display: "inline-block" }}>
						<table className="receipts-table" style={{ width: "fit-content" }}>
							<thead>
								<tr>
									<th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Owner Name</th>
									<th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Number</th>
									<th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Street</th>
									<th style={{ textAlign: "left", whiteSpace: "nowrap" }}>City</th>
									<th style={{ textAlign: "left", whiteSpace: "nowrap" }}>District</th>
									<th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Status</th>
								</tr>
							</thead>
							<tbody>
								{searchResults.map((r) => (
									<tr key={r.id} style={{ cursor: "pointer" }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "gold")} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")} onClick={() => handleSelectOwner(r)}>
										<td style={{ whiteSpace: "nowrap" }}>{r.owner_name}</td>
										<td style={{ whiteSpace: "nowrap" }}>{r.number_addr1}</td>
										<td style={{ whiteSpace: "nowrap" }}>{r.street_addr1}</td>
										<td style={{ whiteSpace: "nowrap" }}>{r.city_name}</td>
										<td style={{ whiteSpace: "nowrap" }}>{r.tax_district}</td>
										<td style={{ whiteSpace: "nowrap" }}>{r.status}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</fieldset>

			{/* Owner detail + Applications */}
			{activeSection === "owner" && (
				<div style={{ display: "flex", gap: "1rem", justifyContent: "center", alignItems: "flex-start", flexWrap: "wrap" }}>

					<fieldset style={{ width: "fit-content" }}>
						<legend>Owner Information</legend>
						<OwnerForm
							form={ownerForm}
							onChange={ownerChange}
							onOperatorToggle={handleOwnerOperatorToggle}
							onSave={handleOwnerSave}
							onCancel={() => setOwnerForm(owner ?? blankOwner)}
						/>
						{ownerStatus === "success" && <StatusPopup message="Saved!" />}
						<ErrorPopup message={ownerError} onClose={() => setOwnerError("")} />
					</fieldset>

					<fieldset style={{ width: "fit-content" }}>
						<legend>Operator Application: July 1, {searchFiscalYear - 1} to June 30, {searchFiscalYear}</legend>
						<table style={{ border: "none" }}>
							<tbody>
								<tr style={{ backgroundColor: "transparent" }}>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Status:</label></td>
									<td style={{ border: "none" }}>
										<input type="text" value={opForm.status || "Pending"} readOnly
											title="Set by OFFICIAL USE in the Qualification section (saved)"
											style={{ width: "100px", border: "none", backgroundColor: "transparent", fontWeight: "bold" }} />
									</td>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Primary County:</label></td>
									<td style={{ border: "none" }}><input type="checkbox" checked={opForm.primary_county} onChange={opChange("primary_county")} /></td>
								</tr>
								<tr style={{ backgroundColor: "transparent" }}>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Operator Name:</label></td>
									<td colSpan={3} style={{ border: "none" }}><input type="text" value={opForm.operator_name || ""} onChange={(e) => setOpForm((p) => ({ ...p, operator_name: e.target.value }))} style={{ width: "100%" }} /></td>
								</tr>
								<tr style={{ backgroundColor: "transparent" }}>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Email:</label></td>
									<td style={{ border: "none" }}><input type="text" value={opForm.email} onChange={opChange("email")} /></td>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Phone:</label></td>
									<td style={{ border: "none" }}><input type="text" value={opForm.phone} onChange={handleOpFormPhoneChange} /></td>
								</tr>
								<tr style={{ backgroundColor: "transparent" }}>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Number:</label></td>
									<td style={{ border: "none" }}><input type="text" value={opForm.number_addr1} onChange={(e) => setOpForm(p => ({ ...p, number_addr1: e.target.value.replace(/[^\d]/g, "") }))} style={{ width: "60px" }} /></td>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Street:</label></td>
									<td style={{ border: "none" }}><input type="text" value={opForm.street_addr1} onChange={(e) => setOpForm(p => ({ ...p, street_addr1: title_Case(e.target.value) }))} /></td>
								</tr>
								<tr style={{ backgroundColor: "transparent" }}>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Addr2:</label></td>
									<td style={{ border: "none" }}><input type="text" value={opForm.addr2} onChange={(e) => setOpForm(p => ({ ...p, addr2: title_Case(e.target.value) }))} /></td>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>City Name:</label></td>
									<td style={{ border: "none" }}><input type="text" value={opForm.city_name} onChange={(e) => setOpForm(p => ({ ...p, city_name: title_Case(e.target.value) }))} /></td>
								</tr>
								<tr style={{ backgroundColor: "transparent" }}>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>State Code:</label></td>
									<td style={{ border: "none" }}><input type="text" value={opForm.state_code} onChange={(e) => opChange("state_code")({ target: { value: e.target.value.toUpperCase() } })} style={opForm.state_code && !valid_state_code(opForm.state_code) ? { width: "40px", border: "4px solid red" } : { width: "40px" }} maxLength={2} /></td>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Zip:</label></td>
									<td style={{ border: "none" }}><input type="text" value={opForm.zip} onChange={(e) => setOpForm(p => ({ ...p, zip: e.target.value.replace(/[^\d-]/g, "") }))} style={{ width: "90px" }} /></td>
								</tr>
								<tr style={{ backgroundColor: "transparent" }}>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Veteran:</label></td>
									<td style={{ border: "none" }}><input type="checkbox" checked={opForm.veteran} onChange={opChange("veteran")} /></td>
								</tr>
							</tbody>
						</table>
						<div style={{ textAlign: "center", marginTop: "0.5rem" }}>
							<button className="saveBtn" type="button" onClick={handleSaveOpForm} disabled={opFormStatus === "submitting" || !valid_state_code(opForm.state_code || "")}>Save</button>
						</div>
						{opFormStatus === "success" && <StatusPopup message="Saved." />}
					<ErrorPopup message={opFormError} onClose={() => setOpFormError("")} />
					</fieldset>


				</div>
			)}

			{/* ── Land / Parcels ── */}
			{activeSection === "land" && (
				<div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap", justifyContent: "center" }}>
				<fieldset style={{ width: "fit-content" }}>
					<legend>Farm Operation Description — {fyLabel(searchFiscalYear)}</legend>
					<div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<input type="text" value={acresOwned || ""} onChange={(e) => setAcresOwned(e.target.value.replace(/[^\d.]/g, ""))} style={{ width: "90px", textAlign: "right" }} />
							<label>1a Acres Owned</label>
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<input type="text" value={acresRentedFrom || ""} onChange={(e) => setAcresRentedFrom(e.target.value.replace(/[^\d.]/g, ""))} style={{ width: "90px", textAlign: "right" }} />
							<label>1b Acres Rented from others</label>
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<input type="text" value={acresRentedTo || ""} onChange={(e) => setAcresRentedTo(e.target.value.replace(/[^\d.]/g, ""))} style={{ width: "90px", textAlign: "right" }} />
							<label>1c Acres Rented to others</label>
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<input type="text" value={(parseFloat(acresOwned) || 0) + (parseFloat(acresRentedFrom) || 0) - (parseFloat(acresRentedTo) || 0) || ""} disabled style={{ width: "90px", textAlign: "right", backgroundColor: "transparent", color: Theme === 4 ? "black" : "white" }} />
							<label>1d Total Acres in Farm</label>
						</div>
					</div>
					<div style={{ textAlign: "center", marginTop: "0.5rem" }}>
						<button className="saveBtn" type="button" onClick={handleSaveOpDesc} disabled={opDescStatus === "submitting"}>Save</button>
					</div>
					{opDescStatus === "success" && <StatusPopup message="Farm description saved." />}
				<ErrorPopup message={opDescError} onClose={() => setOpDescError("")} />
				</fieldset>
				<fieldset style={{ width: "fit-content" }}>
					<legend>Parcels — {fyLabel(searchFiscalYear)}</legend>
					<div style={{ overflowX: "auto", display: "inline-block" }}>
						<table style={{ borderCollapse: "collapse", marginBottom: "0.5rem", whiteSpace: "nowrap", width: "fit-content" }}>
							<thead>
								<tr>
									{["Tax District", "Map", "Parcel ID", "Pasture", "Cropland", "Woodland", "Waste", "Other", "Rented", "Rent / Yr", ""].map((h) => (
										<th key={h} style={{ textAlign: h === "Rented" ? "center" : h === "" ? "" : "left", paddingRight: "0.5rem", paddingBottom: "0.25rem" }}>{h}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{parcels.map((row) => (
									<tr key={row.id} style={{ verticalAlign: "middle" }}>
										<td style={{ paddingRight: "0.5rem" }}>
											<select value={row.tax_district} onChange={(e) => handleDistrictChange(row.id, e.target.value)}>
												<option value=""></option>
												{ParcelDistrictList.map((d) => (
													<option key={d.name} value={d.name}>
														{d.district_number ? `${d.district_number} - ${d.name}` : d.name}
													</option>
												))}
											</select>
										</td>
										<td style={{ paddingRight: "0.5rem" }}>
											<select value={row.map_code || ""} onChange={(e) => {
												const mn = e.target.value
												setMapNames((m) => ({ ...m, [row.id]: mn }))
												parcelChange(row.id, "map_code", mn)
												parcelChange(row.id, "parcel_id", "")
												if (mn) setTimeout(() => {
													const input = document.getElementById(`map_parcel_${row.id}`)
													if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length) }
												}, 0)
											}}>
												<option value=""></option>
												{(mapOptions[row.id] || []).map((mn) => (
													<option key={mn} value={mn}>{mn}</option>
												))}
											</select>
										</td>
										<td style={{ paddingRight: "0.5rem" }}>
											<input
												id={`map_parcel_${row.id}`}
												type="text"
												value={fullParcelDisplay(row.id, row)}
												onChange={(e) => handleParcelIdChange(row.id, e.target.value)}
												onBlur={() => handleParcelIdBlur(row.id)}
												onFocus={(e) => {
													const prefix = fullParcelDisplay(row.id, { ...row, parcel_id: "" })
													e.target.setSelectionRange(prefix.length, prefix.length)
												}}
												style={{ width: "180px", borderColor: isParcelIdValid(row.id, row.parcel_id) ? "" : "red" }}
												placeholder="00-XXXX-0000-0000"
											/>
										</td>
										{["pasture_acres", "cropland_acres", "woodland_acres", "waste_acres", "other_acres"].map((field) => (
											<td key={field} style={{ paddingRight: "0.5rem" }}>
												<input type="text" value={row[field] === 0 ? "" : row[field]} onChange={(e) => parcelChange(row.id, field, e.target.value.replace(/[^\d.]/g, ""))} style={{ width: "65px", textAlign: "right" }} />
											</td>
										))}
										<td style={{ textAlign: "center", paddingRight: "0.5rem" }}>
											<input type="checkbox" checked={row.rented} onChange={(e) => parcelChange(row.id, "rented", e.target.checked)} />
										</td>
										<td style={{ paddingRight: "0.5rem" }}>
											<input type="text" value={row.rent_per_year === 0 ? "" : row.rent_per_year} onChange={(e) => parcelChange(row.id, "rent_per_year", e.target.value.replace(/[^\d.]/g, ""))} style={{ width: "80px", textAlign: "right", opacity: row.rented ? 1 : 0.15 }} disabled={!row.rented} />
										</td>
										<td>
											<button type="button" onClick={() => removeParcel(row.id)} style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontWeight: "bold" }}>✕</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "1rem", marginTop: "0.5rem" }}>
						<button type="button" className="saveBtn" onClick={addParcel} style={{ padding: "2px 10px" }}>+ Add Parcel</button>
						{owner && (
							<button type="button" className="saveBtn" onClick={handleSaveParcels} disabled={parcelStatus === "submitting" || parcels.some((row) => !parcelHasAcreage(row))}>
								Save Parcels
							</button>
						)}
						{parcelStatus === "success" && <StatusPopup message="Parcels saved." />}
						<ErrorPopup message={parcelError} onClose={() => setParcelError("")} />
					</div>
				</fieldset>
			</div>)}

			{/* ── Crops ── */}
			{activeSection === "crops" && (
				<fieldset style={{ width: "fit-content", margin: "0 auto" }}>
					<legend>Crops — {fyLabel(searchFiscalYear)}</legend>

					<div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start", flexWrap: "wrap" }}>
						{/* Section 1: Crop rows */}
						<fieldset style={{ width: "fit-content" }}>
							<legend>Named Crops</legend>
							<div style={{ overflowX: "auto" }}>
								<table style={{ borderCollapse: "collapse", marginBottom: "0.5rem", whiteSpace: "nowrap", width: "fit-content" }}>
									<thead>
										<tr>
											<th style={{ textAlign: "left",   paddingRight: "0.5rem" }}>Name</th>
											<th style={{ textAlign: "right",  paddingRight: "0.5rem" }}>Acres</th>
											<th style={{ textAlign: "right",  paddingRight: "0.5rem" }}>Sqft</th>
											<th style={{ textAlign: "center", paddingRight: "0.5rem" }}>Vegetable</th>
											<th style={{ textAlign: "center", paddingRight: "0.5rem" }}>High Tunnel</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
									{cropRows.map((row) => {
										const usedByOthers = new Set(cropRows.filter((r) => r.id !== row.id).map((r) => r.name))
										return (
											<tr key={row.id} style={{ verticalAlign: "middle" }}>
											<td style={{ paddingRight: "0.5rem" }}>
												{STANDARD_CROP_NAMES.has(row.name)
													? <span style={{ display: "inline-block", width: "240px" }}>{row.name}</span>
													: (row.name !== "" && !CROP_NAMES.includes(row.name))
														? <input type="text" value={row.name === "__custom__" ? "" : row.name} autoFocus onChange={(e) => cropRowChange(row.id, "name", title_Case(e.target.value.replace(/[^a-zA-Z\s]/g, "")) || "__custom__")} placeholder="Crop name" style={{ width: "240px" }} />
														: <select value={row.name} onChange={(e) => cropRowChange(row.id, "name", e.target.value)} style={{ width: "240px" }}>
															<option value="">-- Select --</option>
															{CROP_NAMES.filter((n) => !STANDARD_CROP_NAMES.has(n) && (n === row.name || !usedByOthers.has(n))).map((n) => <option key={n} value={n}>{n}</option>)}
															<option value="__custom__">Custom</option>
														  </select>
												}
												</td>
												<td style={{ paddingRight: "0.5rem" }}>
													<input type="text" value={row.acres === 0 ? "" : row.acres} onChange={(e) => cropRowChange(row.id, "acres", e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"))} style={{ width: "75px", textAlign: "right" }} />
												</td>
												<td style={{ paddingRight: "0.5rem" }}>
													<input type="text" value={row.sqft === 0 ? "" : row.sqft} onChange={(e) => cropRowChange(row.id, "sqft", e.target.value.replace(/[^\d]/g, ""))} style={{ width: "80px", textAlign: "right" }} />
												</td>
												<td style={{ textAlign: "center", paddingRight: "0.5rem" }}>
													<input type="checkbox" checked={row.vegetable} onChange={(e) => cropRowChange(row.id, "vegetable", e.target.checked)} />
												</td>
												<td style={{ textAlign: "center", paddingRight: "0.5rem" }}>
													<input type="checkbox" checked={row.high_tunnel} onChange={(e) => cropRowChange(row.id, "high_tunnel", e.target.checked)} />
												</td>
												<td>
													{!STANDARD_CROP_NAMES.has(row.name) && (
								<button type="button" onClick={() => removeCropRow(row.id)} style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontWeight: "bold" }}>✕</button>
							)}
												</td>
											</tr>
										)
									})}
									</tbody>
								</table>
							</div>
							<div style={{ marginTop: "0.5rem", display: "flex", gap: "0.5rem" }}>
								<button type="button" className="saveBtn" onClick={addCropRow} style={{ padding: "2px 10px" }}>+ Add Row</button>
								<button className="saveBtn" type="button" onClick={handleSaveCrops} disabled={cropsStatus === "submitting" || cropRows.some(addedCropRowMissingValue)}>Save Crops</button>
							</div>
							{cropsStatus === "success" && <StatusPopup message="Crops saved." />}
						<ErrorPopup message={cropsError} onClose={() => setCropsError("")} />
						</fieldset>

						{/* Hay production box */}
						<fieldset style={{ width: "fit-content" }}>
							<legend>All Hay Production</legend>
							<div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.3rem" }}>
								<input type="text" value={hay.bales === 0 ? "" : hay.bales} onChange={(e) => hayChange("bales", e.target.value.replace(/[^\d]/g, ""))} style={{ width: "80px", textAlign: "right" }} />
								<label>Bales X</label>
								<input type="text" value={hay.lbs_per_bale === 0 ? "" : hay.lbs_per_bale} onChange={(e) => hayChange("lbs_per_bale", e.target.value.replace(/[^\d]/g, ""))} style={{ width: "80px", textAlign: "right" }} />
								<label>Lbs/Bale =</label>
								<input type="text" readOnly value={(parseFloat(hay.bales) * parseFloat(hay.lbs_per_bale)) || ""} style={{ width: "100px", textAlign: "right", background: "transparent", border: "1px solid #ccc", color: "white" }} />
								<label>Lbs</label>
							</div>
							<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
								<input type="text" value={hay.rolls === 0 ? "" : hay.rolls} onChange={(e) => hayChange("rolls", e.target.value.replace(/[^\d]/g, ""))} style={{ width: "80px", textAlign: "right" }} />
								<label>Rolls X</label>
								<input type="text" value={hay.lbs_per_roll === 0 ? "" : hay.lbs_per_roll} onChange={(e) => hayChange("lbs_per_roll", e.target.value.replace(/[^\d]/g, ""))} style={{ width: "80px", textAlign: "right" }} />
								<label>Lbs/Roll =</label>
								<input type="text" readOnly value={(parseFloat(hay.rolls) * parseFloat(hay.lbs_per_roll)) || ""} style={{ width: "100px", textAlign: "right", background: "transparent", border: "1px solid #ccc", color: "white" }} />
								<label>Lbs</label>
							</div>
							<div style={{ marginTop: "0.5rem" }}>
								<button className="saveBtn" type="button" onClick={handleSaveHayProduction} disabled={hayStatus === "submitting" || hayProductionIncomplete()}>Save Production</button>
							</div>
							{hayStatus === "success" && <StatusPopup message="Hay production saved." />}
							<ErrorPopup message={hayError} onClose={() => setHayError("")} />
						</fieldset>
					</div>
				</fieldset>
			)}

			{/* ── Trees ── */}
			{activeSection === "trees" && (
				<fieldset style={{ width: "fit-content", margin: "0 auto" }}>
					<legend>Trees — {fyLabel(searchFiscalYear)}</legend>
					<div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}>
						<fieldset style={{ width: "fit-content" }}>
							<legend>Various Trees</legend>
							<table style={{ borderCollapse: "collapse", marginBottom: "0.5rem", whiteSpace: "nowrap", width: "fit-content" }}>
								<thead>
									<tr>
										<th style={{ textAlign: "left",  paddingRight: "0.5rem" }}>Tree Type</th>
										<th style={{ textAlign: "right", paddingRight: "0.5rem" }}>Tree Count</th>
										<th style={{ textAlign: "right", paddingRight: "0.5rem" }}>Acres</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{treeRows.map((row) => {
										const usedByOthers = new Set(treeRows.filter((r) => r.id !== row.id).map((r) => r.tree_type))
										return (<tr key={row.id} style={{ verticalAlign: "middle" }}>
											<td style={{ paddingRight: "0.5rem" }}>
												{(row.tree_type !== "" && !FRUIT_TREE_TYPES.includes(row.tree_type))
												? <input type="text" value={row.tree_type === "__custom__" ? "" : row.tree_type} autoFocus onChange={(e) => treeRowChange(row.id, "tree_type", title_Case(e.target.value) || "__custom__")} placeholder="Tree type" style={{ width: "140px" }} />
												: <select value={row.tree_type} onChange={(e) => treeRowChange(row.id, "tree_type", e.target.value)} style={{ width: "140px" }}>
													<option value="">-- Select --</option>
													{FRUIT_TREE_TYPES.filter((t) => t === row.tree_type || !usedByOthers.has(t)).map((t) => <option key={t} value={t}>{t}</option>)}
													<option value="__custom__">Custom</option>
												  </select>
											}
											</td>
											<td style={{ paddingRight: "0.5rem" }}>
												<input type="text" value={row.tree_count === 0 ? "" : row.tree_count} onChange={(e) => treeRowChange(row.id, "tree_count", e.target.value.replace(/[^\d]/g, ""))} style={{ width: "90px", textAlign: "right" }} />
											</td>
											<td style={{ paddingRight: "0.5rem" }}>
												<input type="text" value={row.acres === 0 ? "" : row.acres} onChange={(e) => treeRowChange(row.id, "acres", e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"))} style={{ width: "75px", textAlign: "right" }} />
											</td>
											<td>
												<button type="button" onClick={() => removeTreeRow(row.id)} style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontWeight: "bold" }}>✕</button>
											</td>
										</tr>
									) })}
								</tbody>
							</table>
							<div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
								<button type="button" className="saveBtn" onClick={addTreeRow} style={{ padding: "2px 10px" }}>+ Add Row</button>
								{/* Only a row without a tree type is incomplete. Zero counts and no
							    rows at all are both real answers -- the orchard produced nothing,
							    or is gone -- and each must be savable to clear a rolled-forward row. */}
							<button type="button" className="saveBtn" onClick={() => handleSaveTrees("trees")} disabled={treesStatus === "submitting" || !treeRows.every((r) => r.tree_type !== "" && r.tree_type !== "__custom__")}>Save</button>
							</div>
							{treesStatus === "success" && treesSaveSource === "trees" && <StatusPopup message="Trees saved." />}
						<ErrorPopup message={treesError} onClose={() => setTreesError("")} />
						</fieldset>

						<fieldset style={{ width: "fit-content" }}>
							<legend>Sap Trees</legend>
							<table style={{ borderCollapse: "collapse", marginBottom: "0.5rem", whiteSpace: "nowrap", width: "fit-content" }}>
								<thead>
									<tr>
										<th style={{ textAlign: "left",  paddingRight: "0.5rem" }}>Tree Type</th>
										<th style={{ textAlign: "right", paddingRight: "0.5rem" }}>Tap Count</th>
										<th style={{ textAlign: "right", paddingRight: "0.5rem" }}>Gallons</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{sapTreeRows.map((row) => {
										const usedByOthers = new Set(sapTreeRows.filter((r) => r.id !== row.id).map((r) => r.tree_type))
										return (<tr key={row.id} style={{ verticalAlign: "middle" }}>
											<td style={{ paddingRight: "0.5rem" }}>
												{(row.tree_type !== "" && !SAP_TREE_TYPES.includes(row.tree_type))
												? <input type="text" value={row.tree_type === "__custom__" ? "" : row.tree_type} autoFocus onChange={(e) => sapTreeRowChange(row.id, "tree_type", title_Case(e.target.value) || "__custom__")} placeholder="Tree type" style={{ width: "140px" }} />
												: <select value={row.tree_type} onChange={(e) => sapTreeRowChange(row.id, "tree_type", e.target.value)} style={{ width: "140px" }}>
													<option value="">-- Select --</option>
													{SAP_TREE_TYPES.filter((t) => t === row.tree_type || !usedByOthers.has(t)).map((t) => <option key={t} value={t}>{t}</option>)}
													<option value="__custom__">Custom</option>
												  </select>
											}
											</td>
											<td style={{ paddingRight: "0.5rem" }}>
												<input type="text" value={row.tree_count === 0 ? "" : row.tree_count} onChange={(e) => sapTreeRowChange(row.id, "tree_count", e.target.value.replace(/[^\d]/g, ""))} style={{ width: "90px", textAlign: "right" }} />
											</td>
											<td style={{ paddingRight: "0.5rem" }}>
												<input type="text" value={row.gallons === 0 ? "" : row.gallons} onChange={(e) => sapTreeRowChange(row.id, "gallons", e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"))} style={{ width: "75px", textAlign: "right" }} />
											</td>
											<td>
												<button type="button" onClick={() => removeSapTreeRow(row.id)} style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontWeight: "bold" }}>✕</button>
											</td>
										</tr>
									) })}
								</tbody>
							</table>
							<div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
								<button type="button" className="saveBtn" onClick={addSapTreeRow} style={{ padding: "2px 10px" }}>+ Add Row</button>
								<button type="button" className="saveBtn" onClick={() => handleSaveTrees("sap")} disabled={treesStatus === "submitting" || !sapTreeRows.every((r) => r.tree_type !== "" && r.tree_type !== "__custom__")}>Save</button>
							</div>
							{treesStatus === "success" && treesSaveSource === "sap" && <StatusPopup message="Trees saved." />}
						<ErrorPopup message={treesError} onClose={() => setTreesError("")} />
						</fieldset>
					</div>

				</fieldset>
			)}

			{/* ── Livestock ── */}
			{activeSection === "livestock" && (() => {
				const enterAsTab = (e) => {
					if (e.key !== "Enter") return
					e.preventDefault()
					const all = Array.from(document.querySelectorAll("input:not([type='checkbox']):not([disabled]), select:not([disabled])"))
					const idx = all.indexOf(e.target)
					if (idx >= 0 && idx < all.length - 1) all[idx + 1].focus()
				}
				return (<>
					<div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "1rem", justifyContent: "center" }}>
					<fieldset style={{ width: "fit-content" }}>
						<legend>Livestock — {fyLabel(searchFiscalYear)}</legend>
						<table style={{ borderCollapse: "collapse", marginBottom: "0.5rem" }}>
							<thead>
								<tr>
									<th style={{ textAlign: "left", paddingRight: "0.5rem" }}>Animal Type</th>
									<th style={{ textAlign: "right", paddingRight: "0.5rem" }}>Count</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{otherLivestock.map((row) => (
									<tr key={row.id}>
										<td style={{ paddingRight: "0.5rem" }}>
											{row.id > DEFAULT_OTHER_LIVESTOCK.length
												? <input type="text" value={row.animal_type} onChange={(e) => otherLivestockChange(row.id, "animal_type", title_Case(e.target.value))} placeholder="Animal type" style={{ width: "240px" }} />
												: <span style={{ display: "inline-block", width: "240px" }}>{row.animal_type}</span>
											}
										</td>
										<td style={{ paddingRight: "0.5rem" }}>
											<input type="text" value={row.count === 0 ? "" : row.count} onChange={(e) => otherLivestockChange(row.id, "count", e.target.value.replace(/[^\d]/g, "") || 0)} onKeyDown={enterAsTab} style={{ width: "70px", textAlign: "right" }} />
										</td>
										<td>
											{row.id > DEFAULT_OTHER_LIVESTOCK.length && (
											<button type="button" onClick={() => removeOtherLivestock(row.id)} style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontWeight: "bold" }}>✕</button>
										)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
						<div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
							<button type="button" className="saveBtn" onClick={addOtherLivestock} style={{ padding: "2px 10px" }}>+ Add Row</button>
							{/* An added row needs an animal type; a count of zero is a real answer. */}
						<button type="button" className="saveBtn" onClick={handleSaveLivestockRows} disabled={livestockRowsStatus === "submitting" || otherLivestock.some((r) => r.id > DEFAULT_OTHER_LIVESTOCK.length && !r.animal_type.trim())}>Save</button>
						</div>
						{livestockRowsStatus === "success" && <StatusPopup message="Livestock saved." />}
						<ErrorPopup message={livestockRowsError} onClose={() => setLivestockRowsError("")} />
					</fieldset>
					<fieldset style={{ width: "fit-content" }}>
						<legend>Poultry — {fyLabel(searchFiscalYear)}</legend>
						{[["hens_pullets", "Hens / Pullets:"], ["broilers", "Broilers:"], ["turkeys", "Turkeys:"]].map(([field, label]) => (
							<Row key={field} label={label} tight>
								<input
									type="text"
									value={livestock[field] === 0 ? "" : livestock[field]}
									onChange={(e) => livestockChange(field, e.target.value.replace(/[^\d]/g, "") || 0)}
									onKeyDown={enterAsTab}
									style={{ width: "80px", textAlign: "right" }}
								/>
							</Row>
						))}
						<Row label="Poultry Owner:" tight>
							<input type="text" value={livestock.poultry_owner} onChange={(e) => livestockChange("poultry_owner", title_Case(e.target.value))} onKeyDown={enterAsTab} style={{ width: "160px" }} />
						</Row>
						<div style={{ marginTop: "0.5rem", textAlign: "center" }}>
							{/* Fixed fields on one record: nothing to validate, and all zeros is
							    how a deputy records that the flock is gone. */}
							<button type="button" className="saveBtn" onClick={handleSavePoultry} disabled={poultryStatus === "submitting"}>Save</button>
						</div>
						{poultryStatus === "success" && <StatusPopup message="Poultry saved." />}
						<ErrorPopup message={poultryError} onClose={() => setPoultryError("")} />
					</fieldset>
					<fieldset style={{ width: "fit-content" }}>
						<legend>Bees — {fyLabel(searchFiscalYear)}</legend>
						{[["bee_colonies", "Colonies:"], ["honey_lbs", "Honey (lbs):"], ["nucs_sold", "Nucs Sold:"], ["queens_sold", "Queens Sold:"]].map(([field, label]) => (
							<Row key={field} label={label} tight>
								<input
									type="text"
									value={livestock[field] === 0 ? "" : livestock[field]}
									onChange={(e) => livestockChange(field, e.target.value.replace(/[^\d.]/g, "") || 0)}
									onKeyDown={enterAsTab}
									style={{ width: "80px", textAlign: "right" }}
								/>
							</Row>
						))}
						<div style={{ marginTop: "0.5rem", textAlign: "center" }}>
							<button type="button" className="saveBtn" onClick={handleSaveBees} disabled={beesStatus === "submitting"}>Save</button>
						</div>
						{beesStatus === "success" && <StatusPopup message="Bees saved." />}
						<ErrorPopup message={beesError} onClose={() => setBeesError("")} />
					</fieldset>
					</div>
				</>)
			})()}

			{/* ── Agricultural Production ── */}
			{activeSection === "production" && (
				<div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap", justifyContent: "center" }}>
				<fieldset style={{ width: "fit-content" }}>
					<legend>Total Production Value</legend>
					<table style={{ border: "none" }}>
						<tbody>
							{[
								["Crop Value:",       cropValue,       setCropValue],
								["Produce Value:",    produceValue,    setProduceValue],
								["Livestock Value:",  livestockValue,  setLivestockValue],
							].map(([label, val, setter]) => (
								<tr key={label} style={{ backgroundColor: "transparent" }}>
									<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>{label}</label></td>
									<td style={{ border: "none" }}>
										<input
											type="text"
											value={val}
											onChange={(e) => setter(e.target.value.replace(/[^\d.]/g, ""))}
											onBlur={(e) => { const n = parseFloat(e.target.value); if (!isNaN(n)) setter(n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })) }}
											onFocus={(e) => { setter(e.target.value.replace(/,/g, "")) }}
											style={{ width: "120px", textAlign: "right" }}
										/>
									</td>
								</tr>
							))}
							<tr style={{ backgroundColor: "transparent" }}>
								<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Total Value:</label></td>
								<td style={{ border: "none" }}>
									<input type="text" readOnly
										value={(() => { const t = (parseFloat(cropValue.replace(/,/g,"")) || 0) + (parseFloat(produceValue.replace(/,/g,"")) || 0) + (parseFloat(livestockValue.replace(/,/g,"")) || 0); return t ? t.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "" })()}
										style={{ width: "120px", textAlign: "right", background: "transparent", color: "white" }}
									/>
								</td>
							</tr>
							<tr style={{ backgroundColor: "transparent" }}>
								<td style={{ textAlign: "right", border: "none", whiteSpace: "nowrap", paddingRight: "0.4rem" }}><label>Agri-Tourism:</label></td>
								<td style={{ border: "none" }}><input type="checkbox" checked={agriTourism} onChange={(e) => setAgriTourism(e.target.checked)} /></td>
							</tr>
						</tbody>
					</table>
					<div style={{ textAlign: "center", marginTop: "0.5rem" }}>
						<button className="saveBtn" type="button" onClick={handleSaveProduction} disabled={prodValueStatus === "submitting"}>Save</button>
					</div>
					{prodValueStatus === "success" && <StatusPopup message="Production saved." />}
					<ErrorPopup message={prodValueError} onClose={() => setProdValueError("")} />
				</fieldset>
				<fieldset style={{ width: "fit-content" }}>
					<legend>Qualifications — {fyLabel(searchFiscalYear)}</legend>
					<p style={{ marginBottom: "0.75rem" }}>
						If the total agricultural production is less than $1,000 check and complete item A, B, C, or D.
						{!productionAboveThreshold && (
							<span style={{ display: "block", fontStyle: "italic", marginTop: "0.25rem" }}>
								Total production value is {productionTotal.toLocaleString("en-US", { style: "currency", currency: "USD" })} — a selection is required before this section can be saved.
							</span>
						)}
					</p>
					{(() => {
						const anyChecked = prodA || prodB || prodC || prodD
						// $1,000 of PRODUCTION, not of income. Farm income is the
						// deputy's figure and can be lower than what was produced.
						const dis = (checked) => productionAboveThreshold || (anyChecked && !checked)
						const fade = (checked) => ({ opacity: (productionAboveThreshold || (anyChecked && !checked)) ? 0.4 : 1 })
						return (<>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
							<input type="checkbox" id="prod_a" checked={prodA} disabled={dis(prodA)} onChange={(e) => { setProdA(e.target.checked); if (!e.target.checked) setProdADetail("") }} />
							<label htmlFor="prod_a" style={fade(prodA)}>A. The land is currently out of production or reduced in scope because</label>
							<input type="text" value={prodADetail} onChange={(e) => setProdADetail(e.target.value)} disabled={dis(prodA)} style={{ flex: 1, ...fade(prodA) }} />
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
							<input type="checkbox" id="prod_b" checked={prodB} disabled={dis(prodB)} onChange={(e) => { setProdB(e.target.checked); if (!e.target.checked) setProdBYear("") }} />
							<label htmlFor="prod_b" style={fade(prodB)}>B. It is currently being developed (for less than 10 years) for agricultural production by the year</label>
							<input type="text" value={prodBYear} onChange={(e) => { const v = e.target.value; if (/^\d{0,4}$/.test(v)) setProdBYear(v) }} onBlur={(e) => { const n = parseInt(e.target.value); if (!e.target.value || n < 2025 || n > 2125) setProdBYear("") }} disabled={dis(prodB)} style={{ width: "50px", ...fade(prodB), border: prodBYear.length === 4 && (parseInt(prodBYear) < 2025 || parseInt(prodBYear) > 2125) ? "3px solid red" : "" }} />
							<span style={fade(prodB)}>Plans are attached.</span>
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
							<input type="checkbox" id="prod_c" checked={prodC} disabled={dis(prodC)} onChange={(e) => { setProdC(e.target.checked); if (!e.target.checked) setProdCCrop("") }} />
							<label htmlFor="prod_c" style={fade(prodC)}>C. Current utilization does not produce a crop each year. Specify crop</label>
							<input type="text" value={prodCCrop} onChange={(e) => { if (/^[a-zA-Z\s]*$/.test(e.target.value)) setProdCCrop(e.target.value) }} disabled={dis(prodC)} style={{ width: "200px", ...fade(prodC) }} />
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
							<input type="checkbox" id="prod_d" checked={prodD} disabled={dis(prodD)} onChange={(e) => setProdD(e.target.checked)} />
							<label htmlFor="prod_d" style={fade(prodD)}>D. Sales of agricultural products last year were at least $500 and the total acres in the farm are less than 5 acres.</label>
						</div>
						</>)
					})()}
					<div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
						<span>2a. Is at least 50% of the total income from this property received from the sale, use, or consumption of agricultural production?</span>
						<label style={{ display: "flex", alignItems: "center", gap: "0.4rem", opacity: q2aDerivable ? 0.6 : 1 }}
							title={q2aDerivable ? "Answered from the Farm and Non-farm income figures below" : "No income recorded — answer by hand"}>
							<input type="checkbox" id="q2a_yes" checked={q2aYes} disabled={q2aDerivable} onChange={(e) => setQ2aManual(e.target.checked ? true : null)} />
							Yes
						</label>
						<label style={{ display: "flex", alignItems: "center", gap: "0.4rem", opacity: q2aDerivable ? 0.6 : 1 }}
							title={q2aDerivable ? "Answered from the Farm and Non-farm income figures below" : "No income recorded — answer by hand"}>
							<input type="checkbox" id="q2a_no" checked={q2aNo} disabled={q2aDerivable} onChange={(e) => setQ2aManual(e.target.checked ? false : null)} />
							No
						</label>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
						<span>Non-farm and Mineral Income</span>
						<input type="text" value={nonFarmIncome} onChange={(e) => setNonFarmIncome(e.target.value.replace(/[^\d]/g, ""))} style={{ width: "150px", textAlign: "right" }} />
						<span>Farm Income:</span>
						<input
							type="text"
							className="f1-input"
							value={farmIncome}
							title="Defaults to Total Production Value. Type a different figure where income earned differs from value produced; clear the field to go back to the total. Press F1 for help."
							onKeyDown={(e) => { if (e.key === "F1") { e.preventDefault(); setShowQualHelp(true) } }}
							onChange={(e) => {
								const v = e.target.value.replace(/[^\d.]/g, "")
								// Clearing the field drops the override, so the total shows again
								// straight away rather than leaving the field blank.
								setFarmIncomeOverride(v === "" ? null : v)
							}}
							onBlur={(e) => { const n = parseFloat(e.target.value.replace(/,/g, "")); if (!isNaN(n)) setFarmIncomeOverride(money(n)) }}
							onFocus={(e) => { const v = e.target.value.replace(/,/g, ""); setFarmIncomeOverride(v === "" ? null : v) }}
							style={{ width: "150px", textAlign: "right" }}
						/>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
						<span>3. If the property owner is a corporation, is farming a main business activity, or if a subsidiary, is farming the main business?</span>
						<label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
							<input type="checkbox" id="q3_yes" checked={q3Yes} onChange={(e) => { setQ3Yes(e.target.checked); if (e.target.checked) setQ3No(false) }} />
							Yes
						</label>
						<label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
							<input type="checkbox" id="q3_no" checked={q3No} onChange={(e) => { setQ3No(e.target.checked); if (e.target.checked) setQ3Yes(false) }} />
							No
						</label>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
						<span>4. Is the property under perpetual conservation easement?</span>
						<label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
							<input type="checkbox" id="q4_yes" checked={q4Yes} onChange={(e) => { setQ4Yes(e.target.checked); if (e.target.checked) setQ4No(false) }} />
							Yes
						</label>
						<label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
							<input type="checkbox" id="q4_no" checked={q4No} onChange={(e) => { setQ4No(e.target.checked); if (e.target.checked) setQ4Yes(false) }} />
							No
						</label>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
						<span>OFFICIAL USE: Granted</span>
						<select value={officialGranted || "Pending"} onChange={(e) => {
								const v = e.target.value
								setOfficialGranted(v)
								// BY line = the assessor (client contact_name), not the logged-in user.
								setOfficialBy(v === "Granted" || v === "Denied" ? (Token.contact_name || "") : "")
							}} style={{ width: "100px" }}>
								<option value="Pending">Pending</option>
								<option value="Granted">Granted</option>
								<option value="Denied">Denied</option>
								<option value="Withdrawn">Withdrawn</option>
							</select>
						<span>By:</span>
						<input type="text" value={officialBy} readOnly title="Set automatically from the county assessor (contact name)" style={{ width: "200px" }} />
					</div>
					<div style={{ textAlign: "center", marginTop: "0.75rem" }}>
						{(() => {
							const missing = []
							if (!productionAboveThreshold && !prodA && !prodB && !prodC && !prodD) missing.push("A, B, C or D")
							if (!q2aYes && !q2aNo) missing.push("2a")
							if (!q3Yes && !q3No) missing.push("3")
							if (!q4Yes && !q4No) missing.push("4")
							return (<>
								{missing.length > 0 && (
									<div style={{ textAlign: "center", fontStyle: "italic", marginBottom: "0.4rem" }}>
										Still to answer: {missing.join(", ")}
									</div>
								)}
								<button className="saveBtn" type="button" onClick={handleSaveQualification} disabled={
									qualStatus === "submitting" || missing.length > 0
								}>Save</button>
							</>)
						})()}
					</div>
					{qualStatus === "success" && <StatusPopup message="Qualification saved." />}
					<ErrorPopup message={qualError} onClose={() => setQualError("")} />
					{showQualHelp && (
						<div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
							onClick={() => setShowQualHelp(false)}>
							<div style={{ background: "var(--fieldset-bg, #fff)", border: "1px solid #888", borderRadius: "6px", padding: "1.5rem 2rem", maxWidth: "620px", width: "90%", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 4px 20px rgba(0,0,0,0.3)", color: "rgb(100, 100, 100)" }}
								onClick={(e) => e.stopPropagation()}>
								<h3 style={{ marginTop: 0, marginBottom: "1rem", color: "rgb(60, 60, 60)" }}>Farm Income and Qualifications — Help</h3>

								<h4 style={{ marginBottom: "0.35rem", color: "rgb(60, 60, 60)" }}>Farm Income</h4>
								<p style={{ marginBottom: "0.75rem" }}>
									Starts as a suggestion equal to the <strong>Total Production Value</strong> and can be
									typed over. On a farm that consumes most of what it grows, the value produced and the
									income earned are different figures — enter what the owner reported, including
									<strong> 0</strong>. Clearing the field returns it to following the total.
								</p>

								<h4 style={{ marginBottom: "0.35rem", color: "rgb(60, 60, 60)" }}>Items A, B, C and D</h4>
								<p style={{ marginBottom: "0.75rem" }}>
									These open only when <strong>total production is under $1,000</strong>, and one must be
									chosen before Qualifications will save. The test uses the production value, not Farm
									Income, so changing the income figure never enables or disables them. Only one item may
									be checked; the others grey out once you pick one.
								</p>

								<h4 style={{ marginBottom: "0.35rem", color: "rgb(60, 60, 60)" }}>Question 2a</h4>
								<p style={{ marginBottom: "0.75rem" }}>
									Answered for you from the two income figures: <strong>Yes</strong> when Farm Income is at
									least half of Farm plus Non-farm income, otherwise <strong>No</strong>. The boxes are
									locked so the answer cannot contradict the numbers beside them — correct the income
									figures to change it. Where no income at all is recorded there is no proportion to take,
									so the boxes unlock and you answer by hand.
								</p>

								<h4 style={{ marginBottom: "0.35rem", color: "rgb(60, 60, 60)" }}>Questions 3 and 4</h4>
								<p style={{ marginBottom: "0.75rem" }}>
									Both start blank and must be answered. Earlier versions showed No on every application
									whether or not anyone had answered, so those old answers were cleared — a blank pair
									means the question is genuinely unanswered, not that the answer was lost.
								</p>

								<h4 style={{ marginBottom: "0.35rem", color: "rgb(60, 60, 60)" }}>If Save is greyed out</h4>
								<p style={{ marginBottom: "1rem" }}>
									A line above the button lists what is still outstanding. Answer each item named there and
									Save becomes available.
								</p>

								<div style={{ textAlign: "center" }}>
									<button className="saveBtn" type="button" onClick={() => setShowQualHelp(false)}>Close</button>
								</div>
							</div>
						</div>
					)}
				</fieldset>
				</div>
			)}

		{showRollForwardConfirm && (
				<div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
					<div style={{ background: "#fff", border: "1px solid #888", borderRadius: "6px", padding: "1.5rem 2rem", maxWidth: "420px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.3)", color: "rgb(80,80,80)" }}>
						<h3 style={{ marginTop: 0, marginBottom: "1rem", color: "rgb(40,40,40)" }}>Application Already Exists</h3>
						<p style={{ marginBottom: "1.5rem" }}>
							A <strong>{fyLabel(searchFiscalYear < CURRENT_FY ? CURRENT_FY : searchFiscalYear + 1)}</strong> application already exists for this owner.
							Do you want to overwrite it with a copy of the <strong>{fyLabel(searchFiscalYear)}</strong> application?
						</p>
						<div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
							<button className="saveBtn" type="button" onClick={handleRollForwardConfirmed}>Overwrite</button>
							<button className="saveBtn" type="button" onClick={() => setShowRollForwardConfirm(false)}>Cancel</button>
						</div>
					</div>
				</div>
			)}

		{showDeleteConfirm && owner && (
				<div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
					<div style={{ background: "#fff", border: "1px solid #888", borderRadius: "6px", padding: "1.5rem 2rem", maxWidth: "420px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.3)", color: "rgb(80,80,80)" }}>
						<h3 style={{ marginTop: 0, marginBottom: "1rem", color: "rgb(40,40,40)" }}>Delete Application</h3>
						<p style={{ marginBottom: "1.5rem" }}>
							Delete the <strong>{fyLabel(searchFiscalYear)}</strong> farm application for <strong>{owner.owner_name}</strong>?
							This permanently removes the application and all of its sections. This cannot be undone.
						</p>
						<div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
							<button className="saveBtn" type="button" onClick={handleDeleteConfirmed} style={{ backgroundColor: "#c0392b", color: "white" }}>Delete</button>
							<button className="saveBtn" type="button" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
						</div>
					</div>
				</div>
			)}
		</main>
	)
}

// ── Owner read-only view ───────────────────────────────────────────────────────

const OwnerReadOnly = ({ owner }) => {
	const td_lbl = { fontWeight: "bold", whiteSpace: "nowrap" }

	const addr = (num, st, a2, city, sc, zip) =>
		`${num} ${st}${a2 ? ", " + a2 : ""}, ${city}, ${sc} ${zip}`

	return (
		<table>
			<tbody>
				<tr>
					<td style={td_lbl}>Tax District:</td>
					<td>{owner.tax_district}</td>
					<td style={td_lbl}>Account ID:</td>
					<td>{owner.account_id}</td>
				</tr>
				<tr>
					<td style={td_lbl}>Name:</td>
					<td colSpan={3}>{owner.owner_name}</td>
				</tr>
				<tr>
					<td style={td_lbl}>Email:</td>
					<td>{owner.email}</td>
					<td style={td_lbl}>Phone:</td>
					<td>{owner.phone}</td>
				</tr>
				<tr>
					<td style={td_lbl}>Address:</td>
					<td colSpan={3}>
						{addr(owner.number_addr1, owner.street_addr1, owner.addr2, owner.city_name, owner.state_code, owner.zip)}
					</td>
				</tr>
				{owner.notes && (
					<tr>
						<td style={td_lbl}>Notes:</td>
						<td colSpan={3}>{owner.notes}</td>
					</tr>
				)}
				<tr>
					<td style={td_lbl}>Operator:</td>
					<td colSpan={3}>
						{owner.operator_is_owner ? "Same as Owner" : owner.operator_name}
					</td>
				</tr>
				{!owner.operator_is_owner && (<>
					<tr>
						<td style={td_lbl}>Op. Address:</td>
						<td colSpan={3}>
							{addr(owner.operator_number_addr1, owner.operator_street_addr1, owner.operator_addr2, owner.operator_city, owner.operator_state, owner.operator_zip)}
						</td>
					</tr>
					<tr>
						<td style={td_lbl}>Op. Email:</td>
						<td>{owner.operator_email}</td>
						<td style={td_lbl}>Op. Phone:</td>
						<td>{owner.operator_phone}</td>
					</tr>
				</>)}
			</tbody>
		</table>
	)
}

// ── Owner add / edit form ─────────────────────────────────────────────────────

const OwnerForm = ({ form, onChange, onOperatorToggle, onSave, onCancel }) => {
	const DistrictList = useStoreState((state) => state.DistrictList)
	const Theme = useStoreState((state) => state.Theme)

	const ownerNameValid = form.owner_name?.trim().length > 0
	const emailValid = !form.email || valid_Email(form.email)
	const phoneValid = !form.phone || valid_PhoneNumber(form.phone)[0]
	const opEmailValid = form.operator_is_owner || !form.operator_email || valid_Email(form.operator_email)
	const opPhoneValid = form.operator_is_owner || !form.operator_phone || valid_PhoneNumber(form.operator_phone)[0]
	const stateValid = valid_state_code(form.state_code || "")
	const canSave = ownerNameValid && emailValid && phoneValid && opEmailValid && opPhoneValid && stateValid

	const handlePhoneChange = (e) => {
		const isDeleting = e.target.value.length < (form.phone || "").length
		const result = valid_PhoneNumber(e.target.value, isDeleting)
		onChange("phone")({ target: { value: result[2] } })
	}

	const handleOpPhoneChange = (e) => {
		const isDeleting = e.target.value.length < (form.operator_phone || "").length
		const result = valid_PhoneNumber(e.target.value, isDeleting)
		onChange("operator_phone")({ target: { value: result[2] } })
	}

	const handleDistrictChange = (e) => {
		const name = e.target.value
		onChange("tax_district")({ target: { value: name } })
		if (name) {
			const dist = DistrictList.find((d) => d.name === name)
			if (dist) {
				onChange("city_name")({ target: { value: dist.city || "" } })
				onChange("state_code")({ target: { value: dist.state_code || "" } })
				onChange("zip")({ target: { value: dist.zip || "" } })
			}
		}
	}

	const L = { textAlign: "right", width: "110px", border: "none" }
	const L2 = { textAlign: "right", width: "100px", border: "none" }
	const V = { textAlign: "left", border: "none" }
	const R = { backgroundColor: "transparent" }
	const invalid = { borderColor: "red" }

	return (
		<form onSubmit={(e) => { e.preventDefault(); onSave() }}>
			<table style={{ border: "none" }}>
				<tbody>
					{/* Tax District */}
					<tr style={R}>
						<td style={L}><label htmlFor="fo_district">Tax District:</label></td>
						<td style={V}>
							<select id="fo_district" value={form.tax_district} onChange={handleDistrictChange}>
								<option value=""></option>
								{DistrictList.map((d) => (
									<option key={d.name} value={d.name}>{d.district_number ? `${d.district_number} - ${d.name}` : d.name}</option>
								))}
							</select>
						</td>
					</tr>
					{/* Owner Name */}
					<tr style={R}>
						<td style={L}><label htmlFor="fo_owner_name">Owner Name:</label></td>
						<td colSpan={3} style={V}>
							<input id="fo_owner_name" type="text" value={form.owner_name || ""} onChange={onChange("owner_name")} style={{ width: "100%", ...(ownerNameValid ? {} : invalid) }} />
						</td>
					</tr>
					{/* Operation Name */}
					<tr style={R}>
						<td style={L}><label htmlFor="fo_operation_name">Operation Name:</label></td>
						<td colSpan={3} style={V}>
							<input id="fo_operation_name" type="text" value={form.operation_name || ""} onChange={onChange("operation_name", title_Case)} style={{ width: "100%" }} />
						</td>
					</tr>
					{/* Email + Phone */}
					<tr style={R}>
						<td style={L}><label htmlFor="fo_email">Email:</label></td>
						<td style={V}>
							<input id="fo_email" type="text" value={form.email} onChange={onChange("email")} style={emailValid ? {} : invalid} />
						</td>
						<td style={L2}><label htmlFor="fo_phone">Phone:</label></td>
						<td style={V}>
							<input id="fo_phone" type="text" value={form.phone} onChange={handlePhoneChange} style={phoneValid ? {} : invalid} />
						</td>
					</tr>
					{/* Number + Street */}
					<tr style={R}>
						<td style={L}><label htmlFor="fo_num">Number:</label></td>
						<td style={V}>
							<input id="fo_num" type="text" value={form.number_addr1} onChange={(e) => onChange("number_addr1")({ target: { value: e.target.value.replace(/[^\d]/g, "") } })} style={{ width: "60px" }} />
						</td>
						<td style={L2}><label htmlFor="fo_street">Street:</label></td>
						<td style={V}>
							<input id="fo_street" type="text" value={form.street_addr1} onChange={onChange("street_addr1", title_Case)} />
						</td>
					</tr>
					{/* Addr2 + City */}
					<tr style={R}>
						<td style={L}><label htmlFor="fo_addr2">Addr2:</label></td>
						<td style={V}>
							<input id="fo_addr2" type="text" value={form.addr2} onChange={onChange("addr2", title_Case)} placeholder="Apt, Unit, etc." />
						</td>
						<td style={L2}><label htmlFor="fo_city">City Name:</label></td>
						<td style={V}>
							<input id="fo_city" type="text" value={form.city_name} onChange={onChange("city_name", title_Case)} />
						</td>
					</tr>
					{/* State + Zip */}
					<tr style={R}>
						<td style={L}><label htmlFor="fo_state">State Code:</label></td>
						<td style={V}>
							<input id="fo_state" type="text" value={form.state_code} onChange={onChange("state_code", (s) => s.toUpperCase())} style={stateValid ? { width: "40px" } : { width: "40px", border: "4px solid red" }} maxLength={2} />
						</td>
						<td style={L2}><label htmlFor="fo_zip">Zip:</label></td>
						<td style={V}>
							<input id="fo_zip" type="text" value={form.zip} onChange={(e) => onChange("zip")({ target: { value: e.target.value.replace(/[^\d-]/g, "") } })} style={{ width: "90px" }} />
						</td>
					</tr>
					{/* Note */}
					<tr style={R}>
						<td style={L}><label htmlFor="fo_notes">Note:</label></td>
						<td colSpan={3} style={V}>
							<textarea id="fo_notes" value={form.notes} onChange={onChange("notes")} style={{ height: "5rem", resize: "none", width: "100%" }} />
						</td>
					</tr>

					{/* ── Operator ── */}
					<tr style={R}>
						<td style={{ ...L, color: Theme === 4 ? "black" : "#d4d4d4" }}>Owner is Operator:</td>
						<td colSpan={3} style={V}>
							<input
								type="checkbox"
								checked={form.operator_is_owner}
								onChange={onOperatorToggle}
							/>
						</td>
					</tr>
				</tbody>
			</table>

			<div style={{ textAlign: "center", marginTop: "0.5rem" }}>
				<button className="saveBtn" type="submit" disabled={!canSave}>Save</button>
				<button className="saveBtn" type="button" style={{ marginLeft: "0.5rem" }} onClick={onCancel}>Cancel</button>
			</div>
		</form>
	)
}

export default Farm
